"use client";

import { useState, useMemo, useEffect, useRef, type Dispatch } from "react";
import {
  FUNNEL_ASSET_EXECUTION_STATUSES,
  FUNNEL_ASSET_SOURCE_SYSTEMS,
  FUNNEL_ASSET_TYPES,
} from "@/lib/funnel-planner/schema";
import type { AppLocale, FunnelExpenseCategory, FunnelWizardState, ProjectionInputs } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { buildCalculationExplanation } from "@/lib/funnel-planner/calculation-explanation";
import { buildSessionCalculationReceipt } from "@/lib/funnel-planner/calculation-receipt-session";
import {
  resolvePlannerPackageAdvice,
  type PlannerPricingSnapshot,
} from "@/lib/funnel-planner/package-adviser";
import { ALL_ADVISORY_FLAGS_OFF, type FunnelAdvisoryFlags } from "@/lib/funnel-planner/advisory-flags";
import { calculateManualSeasonalScenarios } from "@/lib/funnel-planner/seasonal-scenarios";
import type { ManualSeasonalScenarioResult } from "@/lib/funnel-planner/seasonal-scenarios";
import { CalculationExplanationPanel } from "../ui/CalculationExplanationPanel";
import { SeasonalScenarioPanel } from "../ui/SeasonalScenarioPanel";
import {
  SEOH_EXPENSE_PRESETS,
  buildAssetInventory,
  computeMonthlyReinvestmentBudget,
  computeMonthlyExpenseTotal,
  computePnL,
  computeKpis,
  computeFunnelVolumeMetrics,
  sensitivityAnalysis,
  computeMrrProjection,
  scenarioProjection,
  computeScenarioComparison,
  computeReverseGoal,
  computeCapacityBottleneck,
  computeProductRevenueRows,
  computeTotalWaitDays,
  computeActualVsPlan,
  generateTrackingPlan,
  estimateChannelCost,
  totalTrafficCost,
  defaultScenarioSnapshots,
  createExpenseFromPreset,
  type ReverseGoalType,
  type ActualVsPlanRow,
  type TrackingPlanAttributionRole,
  type TrackingPlanTrigger,
} from "@/lib/funnel-planner/engine";
import { summarizeIntegrityWarnings, validateFunnelGraph } from "@/lib/funnel-planner/integrity";
import { getPlannerRecommendations, type FunnelRecommendation } from "@/lib/funnel-planner/recommendations";
import { getChannelDef } from "@/lib/funnel-planner/channels";
import { ProjectionChart } from "@/components/funnel-planner/ui/ProjectionChart";
import { FunnelFlowDiagram } from "@/components/funnel-planner/ui/FunnelFlowDiagram";
import { track } from "@/lib/posthog/events";
import {
  formatFunnelCurrency,
  formatFunnelNumber,
  formatFunnelPercent,
  channelLabel,
  funnelText,
} from "@/lib/funnel-planner/i18n";
import {
  joinAuditTicketGroups,
  STEP_SIX_CTA_TICKETS,
  STEP_SIX_FIELD_INPUT_TICKETS,
  STEP_SIX_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
  STEP_SIX_PROJECTION_KPI_SUPPORT_TICKETS,
  STEP_SIX_SCENARIO_CONTROL_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

type ProjectionStepProps = {
  state: FunnelWizardState;
  dispatch: Dispatch<ProjectionAction>;
  content: FunnelPlannerContent;
  locale: AppLocale;
  pricingSnapshot?: PlannerPricingSnapshot | null;
  advisoryFlags?: FunnelAdvisoryFlags;
  /** True when viewing someone else's shared plan: advisory inputs are read-only. */
  advisoryReadOnly?: boolean;
};

type ProjectionAction =
  | { type: "SET_SEASONAL_DEMAND"; seasonalDemand: FunnelWizardState["seasonalDemand"] }
  | { type: "SET_CAPACITY_CONFIG"; capacityConfig: FunnelWizardState["capacityConfig"] }
  | { type: "SET_SCENARIOS"; scenarios: FunnelWizardState["scenarios"] }
  | { type: "SET_EXPERIMENTS"; experiments: FunnelWizardState["experiments"] }
  | { type: "SET_STAGES"; stages: FunnelWizardState["stages"] }
  | { type: "SET_EXPENSES"; expenses: FunnelWizardState["expenses"] }
  | { type: "SET_PROJECTION_INPUT"; key: keyof ProjectionInputs; value: number }
  | { type: "SET_PROJECTION_INPUTS"; inputs: FunnelWizardState["projectionInputs"] };

type Scenario = "conservative" | "target" | "optimistic";
const SCENARIO_CONTROL_READINESS_TICKET =
  "P1-653001-interactive-step-06-scenario-control-readiness-board";
const SCENARIO_CONTROL_OFFER_ASSEMBLY_TICKET =
  "P3-653002-interactive-step-06-scenario-control-offer-assembly-configurator";
const PROJECTION_KPI_OFFER_ASSEMBLY_TICKET =
  "P2-654001-pricing-step-06-projection-kpis-offer-assembly-configurator-pr";
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";
const STEP_SIX_PROJECTION_ACTION_TICKETS = Array.from(new Set([
  ...STEP_SIX_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
  ...STEP_SIX_CTA_TICKETS,
]));
const SCENARIO_CONTROL_AUDIT_TICKET = joinAuditTicketGroups(
  SCENARIO_CONTROL_READINESS_TICKET,
  SCENARIO_CONTROL_OFFER_ASSEMBLY_TICKET,
  STEP_SIX_SCENARIO_CONTROL_SUPPORT_TICKETS,
  STEP_SIX_FIELD_INPUT_TICKETS,
);
const PROJECTION_KPI_AUDIT_TICKET = joinAuditTicketGroups(
  PROJECTION_KPI_OFFER_ASSEMBLY_TICKET,
  STEP_SIX_PROJECTION_KPI_SUPPORT_TICKETS,
);

function stepSixOfferAssemblyCtaTicketsForAction(actionIndex: number, actionCount: number): string {
  return STEP_SIX_PROJECTION_ACTION_TICKETS
    .filter((_, ticketIndex) => ticketIndex % Math.max(actionCount, 1) === actionIndex)
    .join(";");
}

function stepSixOfferAssemblyCtaAttributes(ticketGroup: string, active: boolean) {
  return {
    "data-funnel-planner-offer-assembly-cta": "true",
    "data-funnel-planner-step-six-offer-assembly-cta": "true",
    "data-audit-ticket": ticketGroup,
    "data-tier-one-family": "offer_assembly_mini_cta_brief",
    "data-gamification-current-state": active ? "complete_result" : "active_inprogress",
    "data-gamification-reduced-motion-static": "supported",
    "data-gamification-supported-states": READINESS_SUPPORTED_STATES,
    "data-readiness-ready-count": active ? 1 : 0,
    "data-readiness-total-count": 1,
    "data-readiness-tier": active ? "brief_piece_ready" : "brief_piece_available",
    "data-blockers-bucket": active ? "none" : "one",
  } as const;
}
type ReportTab = "projection" | "execution" | "actuals" | "reports";
type ExperimentDraft = Omit<FunnelWizardState["experiments"][number], "id">;

const CAPACITY_DEFAULTS: NonNullable<FunnelWizardState["capacityConfig"]> = {
  maxCallsPerMonth: 80,
  salesRepCount: 1,
  hoursPerRepPerMonth: 80,
  avgCallDurationMins: 45,
  closeRateByRepPct: 25,
};

const SCENARIO_DRAFT_DEFAULTS = {
  name: "",
  cvrMultiplier: 1.1,
  visitorMultiplier: 1.1,
  revenueMultiplier: 1,
  notes: "",
};

type ProjectionReceiptInputSource = {
  readonly projectionInputs: Pick<ProjectionInputs, "monthlyVisitors">;
  readonly seasonalDemand: Pick<
    FunnelWizardState["seasonalDemand"],
    "enabled" | "lowerPercent" | "expectedPercent" | "upperPercent"
  >;
  readonly packageRecommendation: Pick<
    FunnelWizardState["packageRecommendation"],
    "enabled" | "decision" | "selectedPackageId"
  >;
};

/**
 * Converts visitor-owned percentage assumptions to a deliberately coarse
 * receipt descriptor. The descriptor is hashed for session provenance, so it
 * must not preserve enough detail to recover a visitor's exact assumption.
 */
export function toSeasonalPercentageReceiptBand(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "not-provided";
  if (value < 0) return "below-baseline";
  if (value === 0) return "baseline";
  if (value <= 10) return "increase-1-to-10";
  if (value <= 25) return "increase-11-to-25";
  if (value <= 50) return "increase-26-to-50";
  return "increase-over-50";
}

/**
 * Returns the value-free, categorical input descriptor used by the live
 * session receipt. Keep raw visitor counts and percentages out of this object:
 * canonical hashing is deterministic, but it is not a privacy boundary for a
 * low-cardinality raw input space.
 */
export function buildProjectionReceiptHashInputs({
  projectionInputs,
  seasonalDemand,
  packageRecommendation,
}: ProjectionReceiptInputSource) {
  const monthlyVisitors = projectionInputs.monthlyVisitors;
  const monthlyVisitorsBand = !Number.isFinite(monthlyVisitors) || monthlyVisitors < 1
    ? "invalid"
    : monthlyVisitors < 100
      ? "1-to-99"
      : monthlyVisitors < 500
        ? "100-to-499"
        : monthlyVisitors < 1_000
          ? "500-to-999"
          : monthlyVisitors < 5_000
            ? "1k-to-4.9k"
            : monthlyVisitors < 10_000
              ? "5k-to-9.9k"
              : monthlyVisitors < 100_000
                ? "10k-to-99.9k"
                : monthlyVisitors < 1_000_000
                  ? "100k-to-999.9k"
                  : "1m-plus";

  return {
    monthlyVisitorsBand,
    seasonalEnabled: seasonalDemand.enabled,
    seasonalLowerBand: toSeasonalPercentageReceiptBand(seasonalDemand.lowerPercent),
    seasonalExpectedBand: toSeasonalPercentageReceiptBand(seasonalDemand.expectedPercent),
    seasonalUpperBand: toSeasonalPercentageReceiptBand(seasonalDemand.upperPercent),
    packageEnabled: packageRecommendation.enabled,
    packageDecision: packageRecommendation.decision,
    packageOverride: packageRecommendation.selectedPackageId,
  };
}

/**
 * Returns value-free output descriptors for the session receipt. Seasonal
 * calculations can still display their precise values in the local UI, but
 * receipt provenance only records which scenario bands were produced.
 */
export function buildProjectionReceiptHashOutputs(
  seasonalResult: Pick<ManualSeasonalScenarioResult, "readiness" | "scenarios">,
) {
  return {
    seasonalReadiness: seasonalResult.readiness,
    seasonalScenarioBands: seasonalResult.scenarios.map(({ scenario, percent }) => ({
      scenario,
      percentageBand: toSeasonalPercentageReceiptBand(percent),
    })),
  };
}

const EXPERIMENT_DRAFT_DEFAULTS: ExperimentDraft = {
  stageId: "",
  hypothesis: "",
  variantA: "Control",
  variantB: "Variant",
  trafficSplitPct: 50,
  primaryKpi: "cvr",
  status: "idea",
  winner: "none",
  actualUpliftPct: undefined,
};

const TRACKING_STATUS_OPTIONS: Array<{
  value: NonNullable<FunnelWizardState["stages"][number]["trackingStatus"]>;
  key: keyof FunnelPlannerContent;
  fallback: string;
}> = [
  { value: "not_set", key: "projectionTrackingStatusNotSet", fallback: "Not set" },
  { value: "none", key: "projectionTrackingStatusNone", fallback: "No tracking" },
  { value: "planned", key: "projectionTrackingStatusPlanned", fallback: "Planned" },
  { value: "utm_only", key: "projectionTrackingStatusUtmOnly", fallback: "UTM only" },
  { value: "pixel", key: "projectionTrackingStatusPixel", fallback: "Pixel" },
  { value: "installed", key: "projectionTrackingStatusInstalled", fallback: "Installed" },
  { value: "server_side", key: "projectionTrackingStatusServerSide", fallback: "Server-side" },
  { value: "crm_matched", key: "projectionTrackingStatusCrmMatched", fallback: "CRM matched" },
  { value: "verified", key: "projectionTrackingStatusVerified", fallback: "Verified" },
  { value: "broken", key: "projectionTrackingStatusBroken", fallback: "Broken" },
];

const SCENARIO_NAME_KEYS: Record<string, string> = {
  baseline: "projectionScenarioDefaultBaselineName",
  conservative: "projectionScenarioDefaultConservativeName",
  stress: "projectionScenarioDefaultStressName",
  optimistic: "projectionScenarioDefaultOptimisticName",
};

const SCENARIO_ASSUMPTION_KEYS: Record<string, string> = {
  "Current planner inputs": "projectionScenarioAssumptionCurrentInputs",
  "Lower conversion": "projectionScenarioAssumptionLowerConversion",
  "Lower traffic": "projectionScenarioAssumptionLowerTraffic",
  "Lower revenue yield": "projectionScenarioAssumptionLowerRevenueYield",
  "Major CVR drop": "projectionScenarioAssumptionMajorCvrDrop",
  "Traffic under-delivery": "projectionScenarioAssumptionTrafficUnderDelivery",
  "Higher conversion": "projectionScenarioAssumptionHigherConversion",
  "Higher traffic": "projectionScenarioAssumptionHigherTraffic",
  "Higher revenue yield": "projectionScenarioAssumptionHigherRevenueYield",
};

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function localizedScenarioName(
  content: FunnelPlannerContent,
  row: { id: string; name: string },
): string {
  const key = SCENARIO_NAME_KEYS[row.id];
  return key ? funnelText(content, key, row.name) : row.name;
}

function localizedScenarioAssumption(content: FunnelPlannerContent, assumption: string): string {
  const key = SCENARIO_ASSUMPTION_KEYS[assumption];
  return key ? funnelText(content, key, assumption) : assumption;
}

function localizedScenarioAssumptions(
  content: FunnelPlannerContent,
  row: { changedAssumptions: string[]; kind: string },
): string {
  const assumptions = row.changedAssumptions.map((assumption) => localizedScenarioAssumption(content, assumption));
  return assumptions.join(", ") || row.kind;
}

function recommendationSourceLabel(content: FunnelPlannerContent, source: FunnelRecommendation["source"]): string {
  const keys: Record<FunnelRecommendation["source"], string> = {
    benchmark_default: "projectionRecommendationSourceBenchmarkDefault",
    user_input: "projectionRecommendationSourceUserInput",
    calculated: "projectionRecommendationSourceCalculated",
    estimated: "projectionRecommendationSourceEstimated",
  };
  return funnelText(content, keys[source], source.replace(/_/g, " "));
}

function recommendationBudgetLabel(content: FunnelPlannerContent, state: FunnelWizardState): string {
  const budget = state.audienceGoalDetails.budgetCeiling;
  if (budget != null && budget < 1_500) {
    return funnelText(content, "projectionRecommendationBudgetLow", "low-budget");
  }
  if (budget != null && budget >= 3_000) {
    return funnelText(content, "projectionRecommendationBudgetScaling", "scaling-budget");
  }
  return funnelText(content, "projectionRecommendationBudgetBaseline", "baseline");
}

function templateDisplayName(content: FunnelPlannerContent, templateId: FunnelWizardState["templateId"]): string {
  if (!templateId) return "";
  return funnelText(content, `templates.${templateId}.label`, templateId);
}

function stageNameById(state: FunnelWizardState, stageId: string): string | null {
  const direct = state.stages.find((stage) => stage.id === stageId)?.name;
  if (direct) return direct;
  const graphStage = state.graph?.nodes.find((node) => node.id === `stage-${stageId}` || node.id === stageId);
  return graphStage?.data.label ?? null;
}

function firstNumber(value: string): string {
  return value.match(/-?\d+(?:[,.]\d+)*/)?.[0] ?? "";
}

function localizeRecommendation(
  content: FunnelPlannerContent,
  state: FunnelWizardState,
  item: FunnelRecommendation,
): { title: string; detail: string; action: string; source: string } {
  const source = recommendationSourceLabel(content, item.source);

  if (item.id === "tracking-missing") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationTrackingMissingTitle", item.title),
      detail: funnelText(content, "projectionRecommendationTrackingMissingDetail", item.detail),
      action: funnelText(content, "projectionRecommendationTrackingMissingAction", item.action),
    };
  }

  if (item.id.startsWith("channel-recommendation-")) {
    const channelId = item.id.slice("channel-recommendation-".length);
    const channel = getChannelDef(channelId);
    const label = channelLabel(content, channelId, channel?.label ?? channelId);
    return {
      source,
      title: funnelText(content, "projectionRecommendationChannelTitle", item.title, { channel: label }),
      detail: funnelText(content, "projectionRecommendationChannelDetail", item.detail, {
        template: templateDisplayName(content, state.templateId),
        budget: recommendationBudgetLabel(content, state),
      }),
      action: funnelText(content, "projectionRecommendationChannelAction", item.action),
    };
  }

  if (item.id === "no-customers") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationNoCustomersTitle", item.title),
      detail: funnelText(content, "projectionRecommendationNoCustomersDetail", item.detail),
      action: funnelText(content, "projectionRecommendationNoCustomersAction", item.action),
    };
  }

  if (item.id.startsWith("assumption-cvr-")) {
    const stageId = item.id.slice("assumption-cvr-".length);
    const stage = state.stages.find((candidate) => candidate.id === stageId);
    return {
      source,
      title: funnelText(content, "projectionRecommendationStageCvrTitle", item.title),
      detail: funnelText(content, "projectionRecommendationStageCvrDetail", item.detail, {
        stage: stage?.name ?? stageNameById(state, stageId) ?? item.detail.split(" is modeled at ")[0] ?? "",
        cvr: stage?.conversionRate.toFixed(1) ?? firstNumber(item.detail),
      }),
      action: funnelText(content, "projectionRecommendationStageCvrAction", item.action),
    };
  }

  if (item.id === "assumption-traffic-confidence") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationTrafficConfidenceTitle", item.title),
      detail: funnelText(content, "projectionRecommendationTrafficConfidenceDetail", item.detail),
      action: funnelText(content, "projectionRecommendationTrafficConfidenceAction", item.action),
    };
  }

  if (item.id.startsWith("leverage-")) {
    const stageId = item.id.slice("leverage-".length);
    const stage = stageNameById(state, stageId) ?? item.title.replace(/ is the strongest bottleneck$/, "");
    return {
      source,
      title: funnelText(content, "projectionRecommendationLeverageTitle", item.title, { stage }),
      detail: funnelText(content, "projectionRecommendationLeverageDetail", item.detail),
      action: funnelText(content, "projectionRecommendationLeverageAction", item.action),
    };
  }

  if (item.id === "margin-low") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationMarginLowTitle", item.title),
      detail: funnelText(content, "projectionRecommendationMarginLowDetail", item.detail, { margin: firstNumber(item.detail) }),
      action: funnelText(content, "projectionRecommendationMarginLowAction", item.action),
    };
  }

  if (item.id === "assumption-cac-margin") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationCacMarginTitle", item.title),
      detail: funnelText(content, "projectionRecommendationCacMarginDetail", item.detail, { cac: firstNumber(item.detail) }),
      action: funnelText(content, "projectionRecommendationCacMarginAction", item.action),
    };
  }

  if (item.id === "assumption-payback-long") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationPaybackLongTitle", item.title),
      detail: funnelText(content, "projectionRecommendationPaybackLongDetail", item.detail, { months: firstNumber(item.detail) }),
      action: funnelText(content, "projectionRecommendationPaybackLongAction", item.action),
    };
  }

  if (item.id === "roas-low") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationRoasLowTitle", item.title),
      detail: funnelText(content, "projectionRecommendationRoasLowDetail", item.detail, { roas: firstNumber(item.detail) }),
      action: funnelText(content, "projectionRecommendationRoasLowAction", item.action),
    };
  }

  if (item.id === "capacity-bottleneck") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationCapacityTitle", item.title),
      detail: funnelText(content, "projectionRecommendationCapacityDetail", item.detail),
      action: funnelText(content, "projectionRecommendationCapacityAction", item.action),
    };
  }

  if (item.id === "offer-order-bump") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationOrderBumpTitle", item.title),
      detail: funnelText(content, "projectionRecommendationOrderBumpDetail", item.detail),
      action: funnelText(content, "projectionRecommendationOrderBumpAction", item.action),
    };
  }

  if (item.id === "offer-upsell") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationUpsellTitle", item.title),
      detail: funnelText(content, "projectionRecommendationUpsellDetail", item.detail),
      action: funnelText(content, "projectionRecommendationUpsellAction", item.action),
    };
  }

  if (item.id === "experiment-first-test") {
    const stage = item.detail.endsWith(" is the best first place to define a test.")
      ? item.detail.replace(" is the best first place to define a test.", "")
      : "";
    return {
      source,
      title: funnelText(content, "projectionRecommendationFirstExperimentTitle", item.title),
      detail: stage
        ? funnelText(content, "projectionRecommendationFirstExperimentDetail", item.detail, { stage })
        : funnelText(content, "projectionRecommendationFirstExperimentFallbackDetail", item.detail),
      action: funnelText(content, "projectionRecommendationFirstExperimentAction", item.action),
    };
  }

  if (item.id === "baseline-healthy") {
    return {
      source,
      title: funnelText(content, "projectionRecommendationHealthyTitle", item.title),
      detail: funnelText(content, "projectionRecommendationHealthyDetail", item.detail),
      action: funnelText(content, "projectionRecommendationHealthyAction", item.action),
    };
  }

  return { source, title: item.title, detail: item.detail, action: item.action };
}

function FieldHelp({ id, text }: { id: string; text: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={text}
        aria-describedby={id}
        aria-expanded={isOpen}
        className="ms-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-[10px] font-semibold leading-none text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
        onBlur={() => setIsOpen(false)}
        onClick={(event) => {
          event.preventDefault();
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        ?
      </button>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-64 -translate-x-1/2 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-start text-xs font-normal leading-snug text-slate-100 shadow-lg transition-opacity"
        style={{ opacity: isOpen ? 1 : 0, visibility: isOpen ? "visible" : "hidden" }}
      >
        {text}
      </span>
    </span>
  );
}

function LabelWithHelp({
  id,
  label,
  help,
}: {
  id: string;
  label: string;
  help: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <FieldHelp id={id} text={help} />
    </span>
  );
}

export function ProjectionStep({
  state,
  dispatch,
  content,
  locale,
  pricingSnapshot = null,
  advisoryFlags = ALL_ADVISORY_FLAGS_OFF,
  advisoryReadOnly = false,
}: ProjectionStepProps) {
  // NL-010: visitor-owned seasonal scenarios, applied once to monthly visitors.
  const seasonalResult = useMemo(
    () =>
      calculateManualSeasonalScenarios({
        enabled: state.seasonalDemand.enabled,
        baselineMonthlyVisitors: state.projectionInputs.monthlyVisitors,
        assumptions: {
          lowerPercent: state.seasonalDemand.lowerPercent,
          expectedPercent: state.seasonalDemand.expectedPercent,
          upperPercent: state.seasonalDemand.upperPercent,
        },
      }),
    [
      state.projectionInputs.monthlyVisitors,
      state.seasonalDemand.enabled,
      state.seasonalDemand.expectedPercent,
      state.seasonalDemand.lowerPercent,
      state.seasonalDemand.upperPercent,
    ],
  );

  // NL-041: recomputed here so the explanation reports the live rule outcome.
  const packageAdvice = useMemo(
    () =>
      resolvePlannerPackageAdvice({
        audience: state.audience,
        templateId: state.templateId,
        products: state.products,
        regionalSettings: state.regionalSettings,
        pricingSnapshot,
        decision: state.packageRecommendation.decision,
        overrideTier: state.packageRecommendation.selectedPackageId,
      }),
    [
      pricingSnapshot,
      state.audience,
      state.packageRecommendation.decision,
      state.packageRecommendation.selectedPackageId,
      state.products,
      state.regionalSettings,
      state.templateId,
    ],
  );

  // NL-014: the calculation receipt is built in-session on the client and never
  // enters the reducer or any persisted payload. A caller-supplied clock keeps
  // the contract caller-clocked; the client-only clock also avoids a
  // server/client hydration mismatch on the timestamp. Building the receipt is
  // the production call site the receipt chain previously lacked (gap #1), so
  // the explanation's method rows can actually render when the flag is on.
  const [sessionClock, setSessionClock] = useState<Date | null>(null);
  useEffect(() => {
    // Capture a browser-only clock once, after hydration, so the receipt carries
    // a stable session timestamp without a server/client hydration mismatch. The
    // receipt content still tracks the inputs through their hashes below.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- browser-only, runs once after mount.
    setSessionClock(new Date());
  }, []);

  const sessionCalculationReceipt = useMemo(() => {
    if (!advisoryFlags.calculatorBenchmarkEnabled || !sessionClock) return null;
    const built = buildSessionCalculationReceipt({
      funnelStateVersion: state.version,
      outputScale: 0,
      now: sessionClock,
      inputs: buildProjectionReceiptHashInputs(state),
      outputs: buildProjectionReceiptHashOutputs(seasonalResult),
      resultUnit: "seasonal-scenario-bands",
    });
    return built.status === "built" ? built.receipt : null;
  }, [
    advisoryFlags.calculatorBenchmarkEnabled,
    sessionClock,
    seasonalResult,
    state.version,
    state.projectionInputs.monthlyVisitors,
    state.seasonalDemand.enabled,
    state.seasonalDemand.lowerPercent,
    state.seasonalDemand.expectedPercent,
    state.seasonalDemand.upperPercent,
    state.packageRecommendation.enabled,
    state.packageRecommendation.decision,
    state.packageRecommendation.selectedPackageId,
  ]);

  // NL-014: informational, session-only, and never persisted.
  const calculationExplanation = useMemo(
    () =>
      buildCalculationExplanation({
        funnelStateVersion: state.version,
        packageAdvice: state.packageRecommendation.enabled ? packageAdvice : null,
        seasonal: state.seasonalDemand.enabled ? seasonalResult : null,
        receipt: sessionCalculationReceipt,
      }),
    [
      packageAdvice,
      seasonalResult,
      sessionCalculationReceipt,
      state.packageRecommendation.enabled,
      state.seasonalDemand.enabled,
      state.version,
    ],
  );

  // NL-010/014/041 analytics (gap #10). Session-only advisory surfaces: the
  // payloads carry only categorical state, never a raw estimate value. The view
  // event fires once per mount, but only after the post-mount session clock has
  // built the receipt, so `has_receipt` reflects the real state rather than the
  // pre-clock null.
  const explanationViewedRef = useRef(false);
  useEffect(() => {
    if (!advisoryFlags.calculatorBenchmarkEnabled) {
      explanationViewedRef.current = false;
      return;
    }
    if (explanationViewedRef.current || sessionClock === null) return;
    explanationViewedRef.current = true;
    track("funnel_planner_advisory_explanation_viewed", {
      template_id: state.templateId ?? undefined,
      locale,
      has_receipt: calculationExplanation.hasReceipt,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emit once after the clock resolves; hasReceipt is read at fire time.
  }, [advisoryFlags.calculatorBenchmarkEnabled, sessionClock, locale, state.templateId]);

  const formatCurrency = (value: number) => formatFunnelCurrency(value, locale, state.regionalSettings);
  const formatNumber = (value: number) => formatFunnelNumber(value, locale);
  const formatPct = (value: number) => formatFunnelPercent(value, locale);
  const notAvailable = funnelText(content, "notAvailableLabel", "N/A");
  const requiresTrafficCostTitle = funnelText(content, "projectionRequiresTrafficCostTitle", "Requires traffic cost data");
  const roasTooltip = funnelText(content, "projectionRoasTooltip", "Add traffic costs in step 2 to calculate ROAS");
  const [activeScenario, setActiveScenario] = useState<Scenario>("target");
  const [reverseGoalType, setReverseGoalType] = useState<ReverseGoalType>(
    state.audienceGoalDetails.objectiveType === "profit" ||
    state.audienceGoalDetails.objectiveType === "revenue" ||
    state.audienceGoalDetails.objectiveType === "leads" ||
    state.audienceGoalDetails.objectiveType === "customers" ||
    state.audienceGoalDetails.objectiveType === "booked_calls" ||
    state.audienceGoalDetails.objectiveType === "cac" ||
    state.audienceGoalDetails.objectiveType === "payback"
      ? state.audienceGoalDetails.objectiveType
      : "revenue",
  );
  const [reverseGoalValue, setReverseGoalValue] = useState(state.audienceGoalDetails.objectiveValue ?? 50_000);
  const [activeReportTab, setActiveReportTab] = useState<ReportTab>("projection");
  const [scenarioDraft, setScenarioDraft] = useState(SCENARIO_DRAFT_DEFAULTS);
  const [experimentDraft, setExperimentDraft] = useState<ExperimentDraft>(EXPERIMENT_DRAFT_DEFAULTS);
  const [focusedScenario, setFocusedScenario] = useState<Scenario | null>(null);
  const scenarioRefs = useRef<Record<Scenario, HTMLButtonElement | null>>({
    conservative: null,
    target: null,
    optimistic: null,
  });
  const headingRefs = {
    pnl: "proj-heading-pnl",
    kpi: "proj-heading-kpi",
    traffic: "proj-heading-traffic",
    leverage: "proj-heading-leverage",
    mrr: "proj-heading-mrr",
  };
  const firstRenderRef = useRef(true);
  const recommendationsViewedRef = useRef(false);
  const modelHealthTrackedRef = useRef("");
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    }
  }, []);

  const { stages, trafficChannels, projectionInputs, products } = state;
  const expenses = state.expenses;
  const hasProducts = products.length > 0;
  const hasChannels = trafficChannels.length > 0;
  const configuredChannelVisitors = trafficChannels.reduce((sum, channel) => sum + channel.monthlyVisitors, 0);
  const graphSourceNodes = state.graph?.nodes.filter((node) => node.data.isSource) ?? [];
  const graphSourceVisitors = graphSourceNodes.reduce((sum, node) => {
    const channel = trafficChannels.find((candidate) => candidate.id === node.data.channel);
    return sum + (channel?.monthlyVisitors ?? node.data.monthlyVisitors ?? 0);
  }, 0);
  const graphHasStageNodes = state.graph?.nodes.some((node) => node.type === "stage") ?? false;
  const usesMonthlyVisitorsFallback = graphHasStageNodes
    ? graphSourceNodes.length === 0 || graphSourceVisitors <= 0
    : trafficChannels.length === 0 || configuredChannelVisitors <= 0;
  const usesRevenuePerLeadFallback = products.length === 0;
  const monthlyExpenseTotal = computeMonthlyExpenseTotal(expenses);
  const modelHealthWarnings = useMemo(() => validateFunnelGraph(state), [state]);
  const modelHealthSummary = useMemo(() => summarizeIntegrityWarnings(modelHealthWarnings), [modelHealthWarnings]);
  const assetInventory = useMemo(() => buildAssetInventory(stages), [stages]);
  const volumeMetrics = useMemo(
    () => computeFunnelVolumeMetrics(stages, projectionInputs, trafficChannels, state.graph),
    [stages, projectionInputs, trafficChannels, state.graph],
  );
  const totalVisitors = volumeMetrics.visitors;
  const horizonMonths = projectionInputs.projectionMonths;

  const conservative = scenarioProjection(stages, projectionInputs, 0.8, trafficChannels, state.graph);
  const target       = scenarioProjection(stages, projectionInputs, 1.0, trafficChannels, state.graph);
  const optimistic   = scenarioProjection(stages, projectionInputs, 1.2, trafficChannels, state.graph);

  const displayProjection =
    activeScenario === "conservative" ? conservative
    : activeScenario === "optimistic" ? optimistic
    : target;

  const horizonRevenue = displayProjection.points.at(-1)?.revenue ?? 0;

  const pnl = useMemo(() => {
    if (!hasProducts) return null;
    return computePnL(products, stages, trafficChannels, projectionInputs, 0, state.regionalSettings, state.graph, expenses);
  }, [products, stages, trafficChannels, projectionInputs, hasProducts, state.regionalSettings, state.graph, expenses]);

  const kpis = useMemo(() => {
    if (!pnl || !hasProducts) return null;
    return computeKpis(pnl, products, stages, trafficChannels, projectionInputs, state.graph);
  }, [pnl, products, stages, trafficChannels, projectionInputs, hasProducts, state.graph]);
  const projectionKpiReady = hasProducts && Boolean(kpis);
  const projectionKpiRealNumberCount = projectionKpiReady ? Math.round(horizonRevenue) : 0;

  const sensitivity = useMemo(() => {
    if (!hasProducts) return [];
    return sensitivityAnalysis(stages, products, trafficChannels, projectionInputs, state.graph);
  }, [stages, products, trafficChannels, projectionInputs, hasProducts, state.graph]);

  const hasSubscriptionProducts = products.some(
    (p) => p.priceType === "monthly" || p.priceType === "yearly"
  );

  const monthlyNewCustomers = kpis ? kpis.totalCustomers / 12 : 0;

  const modeledGrossRevenue = pnl?.grossRevenue ?? volumeMetrics.customers * projectionInputs.revenuePerLead;
  const trafficCostTotal = pnl?.totalTrafficCost ?? totalTrafficCost(trafficChannels, {
    leads: volumeMetrics.leads,
    customers: volumeMetrics.customers,
    grossRevenue: modeledGrossRevenue,
  });

  const stageVolumes = volumeMetrics.stageVolumes;
  const scenarioRows = useMemo(
    () => computeScenarioComparison(state.scenarios, stages, projectionInputs, trafficChannels, state.graph),
    [state.scenarios, stages, projectionInputs, trafficChannels, state.graph],
  );
  const reverseGoal = useMemo(
    () => computeReverseGoal(reverseGoalType, reverseGoalValue, products, stages, trafficChannels, projectionInputs, 0, state.regionalSettings, state.graph, expenses),
    [reverseGoalType, reverseGoalValue, products, stages, trafficChannels, projectionInputs, state.regionalSettings, state.graph, expenses],
  );
  const capacity = useMemo(() => computeCapacityBottleneck(state), [state]);
  const productRevenueRows = useMemo(
    () => computeProductRevenueRows(products, stages, trafficChannels, projectionInputs, state.regionalSettings, state.graph),
    [products, stages, trafficChannels, projectionInputs, state.regionalSettings, state.graph],
  );
  const recommendations = useMemo(() => getPlannerRecommendations(state), [state]);
  const totalWaitDays = computeTotalWaitDays(stages, state.graph);
  const customScenarios = state.scenarios.filter((scenario) => scenario.kind === "custom" && scenario.id.startsWith("custom-"));
  const capacityConfig = state.capacityConfig ?? CAPACITY_DEFAULTS;
  const capacityEnabled = state.capacityConfig != null;
  const deliveryRows = useMemo(
    () => stages.slice(0, 10).map((stage, index) => ({
      id: stage.id,
      name: stage.name,
      owner: stage.owner?.trim() || funnelText(content, "projectionDeliveryUnassigned", "Unassigned"),
      assetStatus: stage.assetStatus ?? stage.publishedStatus ?? "draft",
      dueInDays: stage.dueInDays ?? 7 + index * 3,
      priority: stage.priority ?? (index <= 2 ? "high" : "medium"),
    })),
    [content, stages],
  );
  const actualRows = useMemo(
    () => computeActualVsPlan(stages, projectionInputs, trafficChannels, state.graph),
    [stages, projectionInputs, trafficChannels, state.graph],
  );
  const trackingPlanRows = useMemo(
    () => generateTrackingPlan(stages),
    [stages],
  );
  const actualBehindCount = actualRows.filter((row) => row.status === "behind").length;
  const actualMissingCount = actualRows.filter((row) => row.status === "missing").length;
  const actualAttentionCount = actualRows.filter((row) => row.status === "behind" || row.status === "stale" || row.status === "partial_period").length;
  const reinvestmentPercent = projectionInputs.reinvestmentPercent ?? 0;
  const projectionChainedCvr = totalVisitors > 0 ? (volumeMetrics.customers / totalVisitors) * 100 : 0;
  const projectionTrafficCost = totalTrafficCost(trafficChannels, {
    leads: volumeMetrics.leads,
    customers: volumeMetrics.customers,
    grossRevenue: target.baselineRevenue,
  });
  const modeledReinvestmentBudget = computeMonthlyReinvestmentBudget(
    target.baselineRevenue,
    reinvestmentPercent,
    projectionInputs.reinvestmentCap,
  );
  const usesReinvestmentCostFallback =
    reinvestmentPercent > 0 &&
    modeledReinvestmentBudget > 0 &&
    totalVisitors > 0 &&
    projectionChainedCvr > 0 &&
    projectionTrafficCost <= 0;
  const fallbackCostPerVisitor = Math.max(
    1,
    (projectionChainedCvr / 100) * projectionInputs.revenuePerLead * 0.15,
  );
  const lowerIsBetterReverseGoal = reverseGoalType === "cac" || reverseGoalType === "payback";
  const reverseGoalGap = lowerIsBetterReverseGoal
    ? Math.max(0, reverseGoal.currentValue - reverseGoal.targetValue)
    : reverseGoal.visitorGap;
  const experimentStageId = experimentDraft.stageId && stages.some((stage) => stage.id === experimentDraft.stageId)
    ? experimentDraft.stageId
    : stages[0]?.id ?? "";

  const updateProjectionInputs = (patch: Partial<ProjectionInputs>) => {
    dispatch({
      type: "SET_PROJECTION_INPUTS",
      inputs: {
        ...projectionInputs,
        ...patch,
      },
    });
  };

  const updateStage = (
    stageId: string,
    patch: Partial<FunnelWizardState["stages"][number]>,
  ) => {
    dispatch({
      type: "SET_STAGES",
      stages: stages.map((stage) => (stage.id === stageId ? { ...stage, ...patch } : stage)),
    });
  };

  const updateExpense = (
    expenseId: string,
    patch: Partial<FunnelWizardState["expenses"][number]>,
  ) => {
    dispatch({
      type: "SET_EXPENSES",
      expenses: expenses.map((expense) => (expense.id === expenseId ? { ...expense, ...patch } : expense)),
    });
  };

  const addExpensePreset = (category: FunnelExpenseCategory) => {
    const next = createExpenseFromPreset(category);
    dispatch({ type: "SET_EXPENSES", expenses: [...expenses, next].slice(0, 32) });
  };

  const removeExpense = (expenseId: string) => {
    dispatch({ type: "SET_EXPENSES", expenses: expenses.filter((expense) => expense.id !== expenseId) });
  };

  const scenarioAssumptions = (
    cvrMultiplier: number,
    visitorMultiplier: number,
    revenueMultiplier: number,
  ) => {
    const assumptions: string[] = [];
    if (Math.abs(cvrMultiplier - 1) >= 0.01) {
      assumptions.push(
        funnelText(content, "projectionScenarioAssumptionCvr", "CVR x{value}", {
          value: cvrMultiplier.toFixed(2),
        }),
      );
    }
    if (Math.abs(visitorMultiplier - 1) >= 0.01) {
      assumptions.push(
        funnelText(content, "projectionScenarioAssumptionTraffic", "Traffic x{value}", {
          value: visitorMultiplier.toFixed(2),
        }),
      );
    }
    if (Math.abs(revenueMultiplier - 1) >= 0.01) {
      assumptions.push(
        funnelText(content, "projectionScenarioAssumptionRevenue", "Revenue yield x{value}", {
          value: revenueMultiplier.toFixed(2),
        }),
      );
    }
    return assumptions.length > 0
      ? assumptions
      : [funnelText(content, "projectionScenarioAssumptionCustom", "Custom assumptions")];
  };

  const handleAddScenario = () => {
    const baseScenarios = state.scenarios.length > 0 ? state.scenarios : defaultScenarioSnapshots();
    const cvrMultiplier = clampNumber(scenarioDraft.cvrMultiplier, 0.1, 3);
    const visitorMultiplier = clampNumber(scenarioDraft.visitorMultiplier, 0.1, 5);
    const revenueMultiplier = clampNumber(scenarioDraft.revenueMultiplier, 0.1, 5);
    const scenario = {
      id: `custom-${Date.now()}`,
      name: (scenarioDraft.name.trim() || funnelText(content, "projectionCustomScenarioDefaultName", "Custom scenario")).slice(0, 80),
      kind: "custom" as const,
      cvrMultiplier,
      visitorMultiplier,
      revenueMultiplier,
      changedAssumptions: scenarioAssumptions(cvrMultiplier, visitorMultiplier, revenueMultiplier),
      notes: scenarioDraft.notes.trim().slice(0, 280) || undefined,
    };
    dispatch({ type: "SET_SCENARIOS", scenarios: [...baseScenarios, scenario].slice(0, 8) });
    setScenarioDraft(SCENARIO_DRAFT_DEFAULTS);
    track("funnel_planner_scenario_created", {
      template_id: state.templateId ?? undefined,
      locale,
      scenario_kind: "custom",
    });
  };

  const updateScenario = (id: string, patch: Partial<FunnelWizardState["scenarios"][number]>) => {
    dispatch({
      type: "SET_SCENARIOS",
      scenarios: state.scenarios.map((scenario) => {
        if (scenario.id !== id || scenario.kind !== "custom") return scenario;
        const next = { ...scenario, ...patch };
        return {
          ...next,
          changedAssumptions: scenarioAssumptions(next.cvrMultiplier, next.visitorMultiplier, next.revenueMultiplier),
        };
      }),
    });
  };

  const removeScenario = (id: string) => {
    dispatch({ type: "SET_SCENARIOS", scenarios: state.scenarios.filter((scenario) => scenario.id !== id) });
  };

  const updateCapacity = (patch: Partial<NonNullable<FunnelWizardState["capacityConfig"]>>) => {
    dispatch({
      type: "SET_CAPACITY_CONFIG",
      capacityConfig: {
        ...capacityConfig,
        ...patch,
      },
    });
  };

  const handleAddExperiment = () => {
    if (state.experiments.length >= 12 || !experimentStageId || experimentDraft.hypothesis.trim().length === 0) return;
    const nextExperiment = {
      ...experimentDraft,
      id: `experiment-${Date.now()}`,
      stageId: experimentStageId,
      hypothesis: experimentDraft.hypothesis.trim().slice(0, 240),
      variantA: (experimentDraft.variantA.trim() || "Control").slice(0, 120),
      variantB: (experimentDraft.variantB.trim() || "Variant").slice(0, 120),
      trafficSplitPct: clampNumber(experimentDraft.trafficSplitPct, 0, 100),
      actualUpliftPct: experimentDraft.actualUpliftPct == null
        ? undefined
        : clampNumber(experimentDraft.actualUpliftPct, -100, 500),
    };
    dispatch({ type: "SET_EXPERIMENTS", experiments: [...state.experiments, nextExperiment] });
    setExperimentDraft({ ...EXPERIMENT_DRAFT_DEFAULTS, stageId: stages[0]?.id ?? "" });
  };

  const updateExperiment = (id: string, patch: Partial<FunnelWizardState["experiments"][number]>) => {
    dispatch({
      type: "SET_EXPERIMENTS",
      experiments: state.experiments.map((experiment) => (
        experiment.id === id ? { ...experiment, ...patch } : experiment
      )),
    });
  };

  const removeExperiment = (id: string) => {
    dispatch({ type: "SET_EXPERIMENTS", experiments: state.experiments.filter((experiment) => experiment.id !== id) });
  };

  const formatReverseGoalValue = (goalType: ReverseGoalType, value: number) => {
    if (goalType === "profit" || goalType === "revenue" || goalType === "cac") return formatCurrency(value);
    if (goalType === "payback") {
      return funnelText(content, "projectionMonthsValue", "{count} mo", { count: value.toFixed(1) });
    }
    return formatNumber(value);
  };

  const formatSignedNumber = (value: number | null, decimals = 0) => {
    if (value == null) return notAvailable;
    const magnitude = decimals > 0 ? value.toFixed(decimals) : formatNumber(Math.round(value));
    return value > 0 ? `+${magnitude}` : magnitude;
  };

  const actualStatusLabel = (status: ActualVsPlanRow["status"]) => {
    if (status === "ahead") return funnelText(content, "projectionActualStatusAhead", "Ahead");
    if (status === "on_track") return funnelText(content, "projectionActualStatusOnTrack", "On track");
    if (status === "behind") return funnelText(content, "projectionActualStatusBehind", "Behind");
    if (status === "partial_period") return funnelText(content, "projectionActualStatusPartial", "Partial period");
    if (status === "stale") return funnelText(content, "projectionActualStatusStale", "Stale");
    return funnelText(content, "projectionActualStatusMissing", "Missing actuals");
  };

  const actualStatusStyle = (status: ActualVsPlanRow["status"]) => {
    if (status === "ahead") return { color: "#16a34a" };
    if (status === "behind") return { color: "#ef4444" };
    if (status === "on_track") return { color: "#60a5fa" };
    if (status === "partial_period") return { color: "#fbbf24" };
    if (status === "stale") return { color: "#f97316" };
    return { color: "#94a3b8" };
  };

  const trackingStatusLabel = (status: NonNullable<FunnelWizardState["stages"][number]["trackingStatus"]>) => {
    const option = TRACKING_STATUS_OPTIONS.find((item) => item.value === status);
    return option ? funnelText(content, option.key, option.fallback) : status.replace(/_/g, " ");
  };

  const trackingTriggerLabel = (trigger: TrackingPlanTrigger) => {
    if (trigger === "page_view") return funnelText(content, "projectionTrackingTriggerPageView", "Page view");
    if (trigger === "form_submit") return funnelText(content, "projectionTrackingTriggerFormSubmit", "Form submit");
    if (trigger === "email_click") return funnelText(content, "projectionTrackingTriggerEmailClick", "Email click");
    if (trigger === "booking_submit") return funnelText(content, "projectionTrackingTriggerBookingSubmit", "Booking or application submit");
    if (trigger === "purchase") return funnelText(content, "projectionTrackingTriggerPurchase", "Purchase or checkout");
    if (trigger === "activation") return funnelText(content, "projectionTrackingTriggerActivation", "Activation event");
    return funnelText(content, "projectionTrackingTriggerCustomEvent", "Custom event");
  };

  const outputMetricLabel = (metric: string) => {
    const metricKeys: Record<string, keyof FunnelPlannerContent> = {
      visitors: "projectionMetricVisitors",
      leads: "projectionMetricLeads",
      qualified_leads: "projectionMetricQualifiedLeads",
      booked_calls: "projectionMetricBookedCalls",
      attendees: "projectionMetricAttendees",
      trials: "projectionMetricTrials",
      customers: "projectionMetricCustomers",
      subscribers: "projectionMetricSubscribers",
      revenue: "projectionMetricRevenue",
    };
    const key = metricKeys[metric];
    return key ? funnelText(content, key, metric.replace(/_/g, " ")) : metric.replace(/_/g, " ");
  };

  const attributionLabel = (role: TrackingPlanAttributionRole) => {
    if (role === "first_touch") return funnelText(content, "projectionTrackingAttributionFirstTouch", "First-touch source");
    if (role === "conversion") return funnelText(content, "projectionTrackingAttributionConversion", "Conversion touch");
    if (role === "retention") return funnelText(content, "projectionTrackingAttributionRetention", "Retention touch");
    return funnelText(content, "projectionTrackingAttributionAssist", "Assist / nurture touch");
  };

  const csvCell = (value: unknown) => {
    let raw = String(value ?? "");
    // Spreadsheets evaluate cells starting with = + - @ tab or CR as formulas, so
    // user text (stage, product, channel names) is prefixed with an apostrophe.
    // Plain numbers, including negatives, are left as numbers.
    if (/^[=+\-@\t\r]/.test(raw) && !/^-?\d+(\.\d+)?$/.test(raw)) {
      raw = `'${raw}`;
    }
    return /[",\n\r]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
  };

  const downloadPlannerFile = (filename: string, mimeType: string, body: string) => {
    const blob = new Blob([body], { type: mimeType });
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
  };

  const handleExportJson = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      templateId: state.templateId,
      locale,
      goal: state.goal,
      regionalSettings: state.regionalSettings,
      audienceGoalDetails: state.audienceGoalDetails,
      projectionInputs,
      graph: state.graph,
      trafficChannels: trafficChannels.map((channel) => ({
        id: channel.id,
        label: channel.label,
        monthlyVisitors: channel.monthlyVisitors,
        acquisitionType: channel.acquisitionType,
        confidence: channel.confidence,
        rampType: channel.rampType,
      })),
      stages: stages.map((stage) => ({
        id: stage.id,
        name: stage.name,
        stageType: stage.stageType,
        conversionRate: stage.conversionRate,
        requiredStage: stage.requiredStage,
        owner: stage.owner,
        assetStatus: stage.assetStatus,
        trackingStatus: stage.trackingStatus,
        waitDays: stage.waitDays,
        dueInDays: stage.dueInDays,
        actualCvr: stage.actualCvr,
        actualVolume: stage.actualVolume,
        actualPeriodStart: stage.actualPeriodStart,
        actualPeriodEnd: stage.actualPeriodEnd,
        actualPeriodDays: stage.actualPeriodDays,
        assetUrl: stage.assetUrl,
        assetType: stage.assetType,
        assetSourceSystem: stage.assetSourceSystem,
        assetThumbnailUrl: stage.assetThumbnailUrl,
        assetExecutionStatus: stage.assetExecutionStatus,
        assetCtaNotes: stage.assetCtaNotes,
      })),
      products: products.map((product) => ({
        id: product.id,
        name: product.name,
        productType: product.productType,
        offerRole: product.offerRole,
        priceType: product.priceType,
        price: product.price,
        cogsPercent: product.cogsPercent,
        refundRatePercent: product.refundRatePercent,
      })),
      scenarios: scenarioRows,
      selectedProjectionHorizon: {
        months: horizonMonths,
        scenario: activeScenario,
        monthlyRevenueLabel: funnelText(content, "projectionHorizonRevenueColumn", "Monthly revenue at month {month}", { month: horizonMonths }),
        monthlyRevenue: displayProjection.points.at(-1)?.revenue ?? 0,
      },
      experiments: state.experiments.map((experiment) => ({
        stageId: experiment.stageId,
        primaryKpi: experiment.primaryKpi,
        status: experiment.status,
        winner: experiment.winner,
        trafficSplitPct: experiment.trafficSplitPct,
        actualUpliftPct: experiment.actualUpliftPct,
      })),
      expenses,
      monthlyExpenseTotal,
      assetInventory,
      modelHealth: modelHealthWarnings,
      deliveryRows,
      actualVsPlan: actualRows,
      trackingPlan: trackingPlanRows,
    };
    downloadPlannerFile("funnel-planner-assumptions.json", "application/json", JSON.stringify(payload, null, 2));
    track("funnel_planner_export_downloaded", {
      template_id: state.templateId ?? undefined,
      locale,
      export_format: "json",
    });
  };

  const handleExportCsv = () => {
    const rows = [
      [
        funnelText(content, "projectionScenarioColumn", "Scenario"),
        funnelText(content, "projectionHorizonRevenueColumn", "Monthly revenue at month {month}", { month: horizonMonths }),
        funnelText(content, "projectionHorizonDeltaColumn", "Change at month {month}", { month: horizonMonths }),
        funnelText(content, "projectionChangedAssumptionsColumn", "Changed assumptions"),
      ],
      ...scenarioRows.map((row) => [
        localizedScenarioName(content, row),
        Math.round(row.horizonRevenue),
        Math.round(row.horizonDeltaVsBaseline),
        localizedScenarioAssumptions(content, row),
      ]),
      [],
      [
        funnelText(content, "projectionDeliveryStageColumn", "Stage"),
        funnelText(content, "projectionDeliveryOwnerColumn", "Owner"),
        funnelText(content, "projectionDeliveryDueColumn", "Due"),
        funnelText(content, "projectionDeliveryStatusColumn", "Asset status"),
      ],
      ...deliveryRows.map((row) => [
        row.name,
        row.owner,
        funnelText(content, "projectionDaysValue", "{count} days", { count: row.dueInDays }),
        row.assetStatus,
      ]),
      [],
      [
        funnelText(content, "projectionExpenseLibraryTitle", "Expense library"),
        funnelText(content, "projectionExpenseCategoryColumn", "Category"),
        funnelText(content, "projectionExpenseAmountColumn", "Amount"),
        funnelText(content, "projectionExpenseFrequencyColumn", "Frequency"),
        funnelText(content, "projectionExpenseMonthlyColumn", "Monthly total"),
      ],
      ...expenses.map((expense) => [
        expense.label,
        expense.category,
        expense.amount,
        expense.frequency,
        Math.round(computeMonthlyExpenseTotal([expense])),
      ]),
      [
        funnelText(content, "projectionTotalLabel", "Total"),
        "",
        "",
        "",
        Math.round(monthlyExpenseTotal),
      ],
      [],
      [
        funnelText(content, "projectionAssetInventoryTitle", "Asset inventory"),
        funnelText(content, "projectionAssetTypeColumn", "Asset type"),
        funnelText(content, "projectionAssetSourceColumn", "Source system"),
        funnelText(content, "projectionAssetUrlColumn", "Asset URL"),
        funnelText(content, "projectionStatusColumn", "Status"),
        funnelText(content, "projectionTrackingStatusColumn", "Tracking"),
      ],
      ...assetInventory.map((row) => [
        row.stageName,
        row.assetType ?? "",
        row.sourceSystem ?? "",
        row.assetUrl ?? "",
        row.status,
        trackingStatusLabel(row.trackingStatus),
      ]),
      [],
      [
        funnelText(content, "projectionActualPlanTitle", "Plan vs actual"),
        funnelText(content, "projectionPlannedVolumeColumn", "Planned volume"),
        funnelText(content, "projectionActualVolumeColumn", "Actual volume"),
        funnelText(content, "projectionActualNormalizedVolumeColumn", "Normalized monthly actual"),
        funnelText(content, "projectionActualPeriodDaysColumn", "Actual period days"),
        funnelText(content, "projectionPlannedCvrColumn", "Planned CVR"),
        funnelText(content, "projectionActualCvrColumn", "Actual CVR"),
        funnelText(content, "projectionStatusColumn", "Status"),
      ],
      ...actualRows.map((row) => [
        row.stageName,
        Math.round(row.plannedVolume),
        row.actualVolume ?? "",
        row.normalizedActualVolume == null ? "" : Math.round(row.normalizedActualVolume),
        row.actualPeriodDays,
        row.plannedCvr,
        row.actualCvr ?? "",
        actualStatusLabel(row.status),
      ]),
      [],
      [
        funnelText(content, "projectionTrackingPlanTitle", "Per-node tracking plan"),
        funnelText(content, "projectionTrackingEventColumn", "Event name"),
        funnelText(content, "projectionTrackingTriggerColumn", "Trigger"),
        funnelText(content, "projectionTrackingGtmFieldsColumn", "GTM fields"),
        funnelText(content, "projectionTrackingPosthogFieldsColumn", "PostHog fields"),
        funnelText(content, "projectionTrackingConversionColumn", "Conversion definition"),
        funnelText(content, "projectionTrackingAttributionColumn", "Attribution"),
        funnelText(content, "projectionStatusColumn", "Status"),
      ],
      ...trackingPlanRows.map((row) => [
        row.stageName,
        row.eventName,
        trackingTriggerLabel(row.trigger),
        row.gtmFields.join("; "),
        row.posthogFields.join("; "),
        funnelText(content, "projectionTrackingConversionDefinition", "Count this node when it produces {metric}.", {
          metric: outputMetricLabel(row.outputMetric),
        }),
        attributionLabel(row.attributionRole),
        trackingStatusLabel(row.trackingStatus),
      ]),
    ];
    downloadPlannerFile(
      "funnel-planner-scenarios.csv",
      "text/csv;charset=utf-8",
      rows.map((row) => row.map(csvCell).join(",")).join("\n"),
    );
    track("funnel_planner_export_downloaded", {
      template_id: state.templateId ?? undefined,
      locale,
      export_format: "csv",
    });
  };

  useEffect(() => {
    if (recommendationsViewedRef.current || recommendations.length === 0) return;
    recommendationsViewedRef.current = true;
    track("funnel_planner_rules_recommendation_viewed", {
      template_id: state.templateId ?? undefined,
      locale,
      recommendation_source: recommendations[0]!.source,
      recommendation_severity: recommendations[0]!.severity,
    });
  }, [locale, recommendations, state.templateId]);

  useEffect(() => {
    const categories = Object.keys(modelHealthSummary).sort();
    const signature = categories.join("|");
    if (!signature || modelHealthTrackedRef.current === signature) return;
    modelHealthTrackedRef.current = signature;
    track("funnel_planner_model_health_warning_viewed", {
      template_id: state.templateId ?? undefined,
      locale,
      warning_categories: categories,
      warning_count: modelHealthWarnings.length,
    });
  }, [locale, modelHealthSummary, modelHealthWarnings.length, state.templateId]);

  const reportTabs: Array<{ id: ReportTab; label: string; help: string }> = [
    {
      id: "projection",
      label: funnelText(content, "projectionTabProjection", "Projection"),
      help: funnelText(content, "projectionTabProjectionHelp", "Model traffic, conversion, revenue, P&L, and sensitivity from the current funnel graph."),
    },
    {
      id: "execution",
      label: funnelText(content, "projectionTabExecution", "Execution"),
      help: funnelText(content, "projectionTabExecutionHelp", "Review expenses, asset coverage, published status, and delivery readiness before handoff."),
    },
    {
      id: "actuals",
      label: funnelText(content, "projectionTabActuals", "Actuals"),
      help: funnelText(content, "projectionTabActualsHelp", "Compare measured stage volume and CVR against the normalized forecast period."),
    },
    {
      id: "reports",
      label: funnelText(content, "projectionTabReports", "Reports"),
      help: funnelText(content, "projectionTabReportsHelp", "Check model health and client handoff outputs before exporting the final plan."),
    },
  ];
  const activeReportTabHelp = reportTabs.find((tab) => tab.id === activeReportTab)?.help ?? "";

  return (
    <div className="funnel-planner-projection space-y-8">
      {advisoryFlags.seasonalDemandEnabled && (
        <SeasonalScenarioPanel
          content={content}
          onChange={(seasonalDemand) => {
            dispatch({ type: "SET_SEASONAL_DEMAND", seasonalDemand });
            track("funnel_planner_advisory_seasonal_changed", {
              template_id: state.templateId ?? undefined,
              locale,
              enabled: seasonalDemand.enabled,
              readiness: seasonalResult.readiness,
            });
          }}
          result={seasonalResult}
          seasonalDemand={state.seasonalDemand}
          readOnly={advisoryReadOnly}
        />
      )}

      {advisoryFlags.calculatorBenchmarkEnabled && (
        <CalculationExplanationPanel content={content} explanation={calculationExplanation} />
      )}

      <section
        role="region"
        aria-labelledby="proj-command-center-title"
        className="rounded-lg border bg-muted/30 p-4"
        data-funnel-planner-projection-command-center
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(18rem,0.7fr)]">
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              {funnelText(content, "step6Title", "Model your projections")}
            </p>
            <h3 id="proj-command-center-title" className="mt-1 text-xl font-bold">
              {funnelText(content, "projectionCommandCenterTitle", "Projection command center")}
            </h3>
          </div>
          <div className="rounded-lg border bg-background/50 p-3 text-sm">
            <p className="font-semibold">{funnelText(content, "projectionModelHealthTitle", "Model health")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {modelHealthWarnings.length === 0
                ? funnelText(content, "projectionModelHealthClear", "No structural warnings are active.")
                : funnelText(content, "projectionModelHealthWarningCount", "{count} warnings need review.", { count: modelHealthWarnings.length })}
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <div className="rounded-lg border bg-background/50 p-3">
            <p className="text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-command-revenue"
                label={funnelText(content, "projectionMonth12Revenue", "Monthly revenue at month {month}", { month: horizonMonths })}
                help={funnelText(content, "projectionCommandCenterRevenueHelp", "Projected monthly revenue at month {month} from the active graph or linear fallback.", { month: horizonMonths })}
              />
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums" data-projection-horizon-revenue>{formatCurrency(target.points.at(-1)?.revenue ?? 0)}</p>
          </div>
          <div className="rounded-lg border bg-background/50 p-3">
            <p className="text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-command-visitors"
                label={funnelText(content, "projectionMetricVisitors", "visitors")}
                help={funnelText(content, "projectionCommandCenterVisitorsHelp", "Total modeled monthly visitors from selected channels or graph source nodes.")}
              />
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums">{formatNumber(totalVisitors)}</p>
          </div>
          <div className="rounded-lg border bg-background/50 p-3">
            <p className="text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-command-customers"
                label={funnelText(content, "projectionMetricCustomers", "customers")}
                help={funnelText(content, "projectionCommandCenterCustomersHelp", "Estimated monthly customers after graph splits, edge CVR, optional paths, waits, loops, and stage CVR.")}
              />
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums">{formatNumber(volumeMetrics.customers)}</p>
          </div>
          <div className="rounded-lg border bg-background/50 p-3">
            <p className="text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-command-expenses"
                label={funnelText(content, "projectionExpenseMonthlyColumn", "Monthly total")}
                help={funnelText(content, "projectionCommandCenterExpensesHelp", "Computed monthly operating cost from one-time, monthly, quarterly, annual, and timed expense rows.")}
              />
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums">{formatCurrency(monthlyExpenseTotal)}</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <label className="fp-pro-field">
            <span>{funnelText(content, "projectionHorizonLabel", "Projection horizon (months, 1-36)")}</span>
            <input
              type="number"
              aria-label={funnelText(content, "projectionHorizonLabel", "Projection horizon (months, 1-36)")}
              min={1}
              max={36}
              step={1}
              value={horizonMonths}
              onChange={(event) => updateProjectionInputs({
                projectionMonths: clampNumber(Math.round(Number(event.target.value)), 1, 36),
              })}
            />
          </label>
          {usesMonthlyVisitorsFallback && (
            <label className="fp-pro-field">
              <span>{funnelText(content, "projectionFallbackMonthlyVisitorsLabel", "Template monthly visitors preset")}</span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionFallbackMonthlyVisitorsLabel", "Template monthly visitors preset")}
                min={1}
                max={10_000_000}
                step={1}
                value={projectionInputs.monthlyVisitors}
                onChange={(event) => updateProjectionInputs({
                  monthlyVisitors: clampNumber(Math.round(Number(event.target.value)), 1, 10_000_000),
                })}
              />
            </label>
          )}
          <label className="fp-pro-field">
            <span>
              <LabelWithHelp
                id="proj-help-revenue-per-final-conversion"
                label={funnelText(content, "projectionFallbackRevenuePerLeadLabel", "Revenue per final-stage conversion")}
                help={funnelText(content, "projectionRevenuePerFinalConversionHelp", "The projection values each final-stage conversion at this amount. Product-level revenue and P&L are reported separately.")}
              />
            </span>
            <input
              type="number"
              aria-label={funnelText(content, "projectionFallbackRevenuePerLeadLabel", "Revenue per final-stage conversion")}
              min={1}
              max={500_000}
              step="any"
              value={projectionInputs.revenuePerLead}
              onChange={(event) => updateProjectionInputs({
                revenuePerLead: clampNumber(Number(event.target.value), 1, 500_000),
              })}
            />
          </label>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {funnelText(content, "projectionZeroGrowthNote", "The baseline assumes 0% automatic month-to-month growth across the selected horizon. Scenario changes and entered reinvestment are shown separately; these are planning scenarios, not forecasts or guarantees.")}
        </p>
        {(usesMonthlyVisitorsFallback || usesRevenuePerLeadFallback) && (
          <p className="mt-2 text-xs text-muted-foreground">
            {funnelText(content, "projectionFallbackPresetNote", "Visitor and revenue-per-lead fallbacks come from the selected template or starting planner inputs. They are editable planning assumptions, not observed industry evidence.")}
          </p>
        )}
      </section>

      {/* Funnel flow header */}
      <div className="rounded-lg border bg-muted/40 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          {funnelText(content, "projectionFunnelLabel", "Your funnel")}
        </p>
        <FunnelFlowDiagram stages={stages} volumes={stageVolumes} compact content={content} locale={locale} />
      </div>

      <section role="region" aria-labelledby="proj-report-workspace-title" data-funnel-planner-report-workspace>
        <div className="fp-pro-section-heading">
          <div>
            <h3 id="proj-report-workspace-title" className="text-lg font-bold">
              {funnelText(content, "projectionReportWorkspaceTitle", "Report workspace")}
            </h3>
            <p>{funnelText(content, "projectionReportWorkspaceHelp", "Switch between projection, execution, actuals, and report outputs without losing the detailed tables below.")}</p>
          </div>
          <div className="flex flex-wrap gap-2" data-funnel-planner-export-controls>
            <button type="button" className="fp-pro-button" onClick={handleExportJson}>
              {funnelText(content, "projectionExportJsonButton", "Export JSON")}
            </button>
            <button type="button" className="fp-pro-button" onClick={handleExportCsv}>
              {funnelText(content, "projectionExportCsvButton", "Export CSV")}
            </button>
          </div>
        </div>
        <div role="tablist" aria-label={funnelText(content, "projectionReportTabsLabel", "Planner report tabs")} className="flex flex-wrap gap-2" data-funnel-planner-report-tabs>
          {reportTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeReportTab === tab.id}
              aria-controls={`proj-report-panel-${tab.id}`}
              id={`proj-report-tab-${tab.id}`}
              title={tab.help}
              className={activeReportTab === tab.id ? "fp-pro-button fp-pro-button--primary" : "fp-pro-button"}
              onClick={() => setActiveReportTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          id={`proj-report-panel-${activeReportTab}`}
          role="tabpanel"
          aria-labelledby={`proj-report-tab-${activeReportTab}`}
          className="mt-3 rounded-lg border bg-muted/20 p-3"
          data-funnel-planner-report-tab={activeReportTab}
        >
          {activeReportTabHelp ? (
            <p className="mb-3 text-sm text-muted-foreground">{activeReportTabHelp}</p>
          ) : null}
          {activeReportTab === "projection" && (
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionMetricVisitors", "visitors")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(totalVisitors)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionMetricLeads", "leads")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(volumeMetrics.leads)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionMetricCustomers", "customers")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(volumeMetrics.customers)}</p>
              </div>
            </div>
          )}
          {activeReportTab === "execution" && (
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionExpenseMonthlyColumn", "Monthly total")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatCurrency(monthlyExpenseTotal)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionAssetInventoryTitle", "Asset inventory")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(assetInventory.length)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionPublishedStatusReportTitle", "Published-status report")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(stages.filter((stage) => stage.publishedStatus === "live").length)}</p>
              </div>
            </div>
          )}
          {activeReportTab === "actuals" && (
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionActualAttentionTitle", "Needs review")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(actualAttentionCount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionActualPlanMissing", "{count} stages still need actuals before drift can be judged.", { count: actualMissingCount })}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(actualMissingCount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{funnelText(content, "projectionActualStatusOnTrack", "On track")}</p>
                <p className="text-lg font-semibold tabular-nums">{formatNumber(actualRows.filter((row) => row.status === "on_track" || row.status === "ahead").length)}</p>
              </div>
            </div>
          )}
          {activeReportTab === "reports" && (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-sm font-semibold">{funnelText(content, "projectionModelHealthTitle", "Model health")}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {modelHealthWarnings.length === 0
                    ? funnelText(content, "projectionModelHealthClear", "No structural warnings are active.")
                    : funnelText(content, "projectionModelHealthWarningCount", "{count} warnings need review.", { count: modelHealthWarnings.length })}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold">{funnelText(content, "projectionClientHandoffChecklistTitle", "Client handoff checklist")}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {funnelText(content, "projectionClientHandoffChecklistHelp", "Confirm model health, asset ownership, tracking status, and exported assumptions before handoff.")}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {activeReportTab === "projection" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="projection">
      {/* SECTION 1: Scenario Toggle + Chart */}
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-2" id="proj-scenario-label">
            <LabelWithHelp
              id="proj-help-scenario-toggle"
              label={funnelText(content, "projectionScenarioTitle", "Revenue Scenario")}
              help={funnelText(content, "projectionHelpScenarioToggle", "Switches the displayed projection between conservative, target, and optimistic assumptions without changing the saved model.")}
            />
          </p>
          <div
            role="radiogroup"
            aria-labelledby="proj-scenario-label"
            className="flex gap-3"
            data-funnel-planner-scenario-control="true"
            data-audit-ticket={SCENARIO_CONTROL_AUDIT_TICKET}
            data-tier-one-family="readiness_board"
            data-gamification-current-state="complete_result"
            data-gamification-reduced-motion-static="supported"
            data-gamification-supported-states={READINESS_SUPPORTED_STATES}
            data-readiness-ready-count={1}
            data-readiness-total-count={1}
            data-readiness-tier="scenario_ready"
            data-blockers-bucket="none"
          >
            <span className="sr-only" role="status" aria-live="polite">
              {funnelText(content, "gamificationScenarioControlReady", "Projection scenario control ready")}
            </span>
            {(["conservative", "target", "optimistic"] as Scenario[]).map((s, scenarioIndex) => {
              const labels: Record<Scenario, string> = {
                conservative: funnelText(content, "projectionScenarioConservative", "Conservative (x0.8)"),
                target: funnelText(content, "projectionScenarioTarget", "Target"),
                optimistic: funnelText(content, "projectionScenarioOptimistic", "Optimistic (x1.2)"),
              };
              const isActive = activeScenario === s;
              const isFocused = focusedScenario === s;
              return (
                <button
                  key={s}
                  {...stepSixOfferAssemblyCtaAttributes(
                    stepSixOfferAssemblyCtaTicketsForAction(scenarioIndex, 3),
                    isActive,
                  )}
                  ref={(el) => { scenarioRefs.current[s] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={funnelText(content, "projectionScenarioAria", "Revenue scenario: {scenario}", { scenario: labels[s] })}
                  onClick={() => {
                    setActiveScenario(s);
                    track("funnel_planner_scenario_compared", {
                      template_id: state.templateId ?? undefined,
                      locale,
                      scenario_kind: s,
                    });
                  }}
                  onFocus={() => setFocusedScenario(s)}
                  onBlur={() => setFocusedScenario(null)}
                  onKeyDown={(e) => {
                    const order: Scenario[] = ["conservative", "target", "optimistic"];
                    const idx = order.indexOf(s);
                    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                      e.preventDefault();
                      const next = order[(idx + 1) % order.length];
                      setActiveScenario(next);
                      scenarioRefs.current[next]?.focus();
                    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                      e.preventDefault();
                      const prev = order[(idx - 1 + order.length) % order.length];
                      setActiveScenario(prev);
                      scenarioRefs.current[prev]?.focus();
                    }
                  }}
                  style={{
                    minHeight: 44,
                    borderRadius: 6,
                    border: `1px solid ${isActive ? "#2563eb" : "#1e293b"}`,
                    background: isActive ? "#2563eb" : "transparent",
                    color: isActive ? "#ffffff" : "#e2e8f0",
                    padding: "10px 14px",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    outline: isFocused ? "2px solid #3b82f6" : "none",
                    outlineOffset: 2,
                    transition: "background 0.15s, border-color 0.15s, color 0.15s",
                  }}
                >
                  <span className="sr-only" role="status" aria-live="polite">
                    {isActive
                      ? funnelText(content, "gamificationOfferAssemblyCtaSelected", "Selected for review")
                      : funnelText(content, "gamificationOfferAssemblyCtaEmptyStatus", "Mark this planner action for the implementation brief.")}
                  </span>
                  {labels[s]}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div className="rounded-lg border p-3" style={{ flex: "0 0 auto" }}>
            <p className="text-xs text-muted-foreground">{funnelText(content, "projectionMonth12Revenue", "Monthly revenue at month {month}", { month: horizonMonths })}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{formatCurrency(horizonRevenue)}</p>
          </div>
        </div>

        <ProjectionChart
          projection={displayProjection}
          content={content}
          locale={locale}
        />
      </div>

      <section role="region" aria-labelledby="proj-scenario-comparison-title">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 id="proj-scenario-comparison-title" className="text-lg font-bold">
            {funnelText(content, "projectionScenarioComparisonTitle", "Scenario comparison")}
          </h3>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="fp-pro-button" onClick={handleExportJson}>
              {funnelText(content, "projectionExportJsonButton", "Export JSON")}
            </button>
            <button type="button" className="fp-pro-button" onClick={handleExportCsv}>
              {funnelText(content, "projectionExportCsvButton", "Export CSV")}
            </button>
          </div>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">{funnelText(content, "projectionScenarioComparisonCaption", "Named scenario comparison by monthly revenue at month {month}", { month: horizonMonths })}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionScenarioColumn", "Scenario")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionHorizonRevenueColumn", "Monthly revenue at month {month}", { month: horizonMonths })}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionHorizonDeltaColumn", "Change at month {month}", { month: horizonMonths })}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionChangedAssumptionsColumn", "Changed assumptions")}</th>
              </tr>
            </thead>
            <tbody>
              {scenarioRows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium">{localizedScenarioName(content, row)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.horizonRevenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: row.horizonDeltaVsBaseline >= 0 ? "#22c55e" : "#ef4444" }}>
                    {row.horizonDeltaVsBaseline >= 0 ? "+" : ""}{formatCurrency(row.horizonDeltaVsBaseline)}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{localizedScenarioAssumptions(content, row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="fp-pro-panel mt-4" data-funnel-planner-scenario-builder>
          <div className="fp-pro-section-heading">
            <div>
              <h4>{funnelText(content, "projectionScenarioBuilderTitle", "Custom scenario builder")}</h4>
              <p>{funnelText(content, "projectionScenarioBuilderHelp", "Create editable snapshots for stress cases, launch plans, or upside assumptions.")}</p>
            </div>
            <button
              type="button"
              className="fp-pro-button fp-pro-button--primary"
              onClick={handleAddScenario}
              disabled={state.scenarios.length >= 8}
            >
              {funnelText(content, "projectionAddScenarioButton", "Add scenario")}
            </button>
          </div>
          <div className="fp-pro-grid fp-pro-grid--5 mt-3">
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-scenario-name"
                  label={funnelText(content, "projectionScenarioNameField", "Scenario name")}
                  help={funnelText(content, "projectionHelpScenarioName", "Name the custom case so it is clear in scenario reports and exports.")}
                />
              </span>
              <input
                type="text"
                aria-label={funnelText(content, "projectionScenarioNameField", "Scenario name")}
                value={scenarioDraft.name}
                onChange={(event) => setScenarioDraft((current) => ({ ...current, name: event.target.value.slice(0, 80) }))}
                placeholder={funnelText(content, "projectionCustomScenarioDefaultName", "Custom scenario")}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-scenario-cvr"
                  label={funnelText(content, "projectionScenarioCvrMultiplierField", "CVR multiplier")}
                  help={funnelText(content, "projectionHelpScenarioCvrMultiplier", "Multiplies every modeled conversion rate in this scenario to test downside or upside funnel efficiency.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionScenarioCvrMultiplierField", "CVR multiplier")}
                min={0.1}
                max={3}
                step={0.05}
                value={scenarioDraft.cvrMultiplier}
                onChange={(event) => setScenarioDraft((current) => ({ ...current, cvrMultiplier: Number(event.target.value) }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-scenario-traffic"
                  label={funnelText(content, "projectionScenarioTrafficMultiplierField", "Traffic multiplier")}
                  help={funnelText(content, "projectionHelpScenarioTrafficMultiplier", "Multiplies modeled visitors to test traffic under-delivery, launch spikes, or growth plans.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionScenarioTrafficMultiplierField", "Traffic multiplier")}
                min={0.1}
                max={5}
                step={0.05}
                value={scenarioDraft.visitorMultiplier}
                onChange={(event) => setScenarioDraft((current) => ({ ...current, visitorMultiplier: Number(event.target.value) }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-scenario-revenue"
                  label={funnelText(content, "projectionScenarioRevenueMultiplierField", "Revenue multiplier")}
                  help={funnelText(content, "projectionHelpScenarioRevenueMultiplier", "Multiplies revenue yield to model pricing, AOV, margin, or offer-mix changes.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionScenarioRevenueMultiplierField", "Revenue multiplier")}
                min={0.1}
                max={5}
                step={0.05}
                value={scenarioDraft.revenueMultiplier}
                onChange={(event) => setScenarioDraft((current) => ({ ...current, revenueMultiplier: Number(event.target.value) }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-scenario-notes"
                  label={funnelText(content, "projectionScenarioNotesField", "Notes")}
                  help={funnelText(content, "projectionHelpScenarioNotes", "Short internal explanation of what changed in this scenario for handoff context.")}
                />
              </span>
              <input
                type="text"
                aria-label={funnelText(content, "projectionScenarioNotesField", "Notes")}
                value={scenarioDraft.notes}
                onChange={(event) => setScenarioDraft((current) => ({ ...current, notes: event.target.value.slice(0, 280) }))}
                placeholder={funnelText(content, "projectionScenarioNotesPlaceholder", "What changed?")}
              />
            </label>
          </div>
          {customScenarios.length > 0 && (
            <div className="fp-pro-edit-list mt-4">
              {customScenarios.map((scenario) => (
                <article key={scenario.id} className="fp-pro-edit-row">
                  <label className="fp-pro-field">
                    <span>{funnelText(content, "projectionScenarioNameField", "Scenario name")}</span>
                    <input
                      type="text"
                      aria-label={funnelText(content, "projectionScenarioNameField", "Scenario name")}
                      value={scenario.name}
                      title={funnelText(content, "projectionHelpScenarioName", "Name the custom case so it is clear in scenario reports and exports.")}
                      onChange={(event) => updateScenario(scenario.id, { name: event.target.value.slice(0, 80) })}
                    />
                  </label>
                  <label className="fp-pro-field">
                    <span>{funnelText(content, "projectionScenarioCvrMultiplierField", "CVR multiplier")}</span>
                    <input
                      type="number"
                      aria-label={funnelText(content, "projectionScenarioCvrMultiplierField", "CVR multiplier")}
                      min={0.1}
                      max={3}
                      step={0.05}
                      value={scenario.cvrMultiplier}
                      title={funnelText(content, "projectionHelpScenarioCvrMultiplier", "Multiplies every modeled conversion rate in this scenario to test downside or upside funnel efficiency.")}
                      onChange={(event) => updateScenario(scenario.id, { cvrMultiplier: clampNumber(Number(event.target.value), 0.1, 3) })}
                    />
                  </label>
                  <label className="fp-pro-field">
                    <span>{funnelText(content, "projectionScenarioTrafficMultiplierField", "Traffic multiplier")}</span>
                    <input
                      type="number"
                      aria-label={funnelText(content, "projectionScenarioTrafficMultiplierField", "Traffic multiplier")}
                      min={0.1}
                      max={5}
                      step={0.05}
                      value={scenario.visitorMultiplier}
                      title={funnelText(content, "projectionHelpScenarioTrafficMultiplier", "Multiplies modeled visitors to test traffic under-delivery, launch spikes, or growth plans.")}
                      onChange={(event) => updateScenario(scenario.id, { visitorMultiplier: clampNumber(Number(event.target.value), 0.1, 5) })}
                    />
                  </label>
                  <label className="fp-pro-field">
                    <span>{funnelText(content, "projectionScenarioRevenueMultiplierField", "Revenue multiplier")}</span>
                    <input
                      type="number"
                      aria-label={funnelText(content, "projectionScenarioRevenueMultiplierField", "Revenue multiplier")}
                      min={0.1}
                      max={5}
                      step={0.05}
                      value={scenario.revenueMultiplier}
                      title={funnelText(content, "projectionHelpScenarioRevenueMultiplier", "Multiplies revenue yield to model pricing, AOV, margin, or offer-mix changes.")}
                      onChange={(event) => updateScenario(scenario.id, { revenueMultiplier: clampNumber(Number(event.target.value), 0.1, 5) })}
                    />
                  </label>
                  <button type="button" className="fp-pro-button fp-pro-button--danger" onClick={() => removeScenario(scenario.id)}>
                    {funnelText(content, "projectionRemoveScenarioButton", "Remove")}
                  </button>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section role="region" aria-labelledby="proj-reverse-goal-title">
        <h3 id="proj-reverse-goal-title" className="text-lg font-bold mb-3">
          {funnelText(content, "projectionReverseGoalTitle", "Reverse goal planner")}
        </h3>
        <div className="grid gap-3 md:grid-cols-[220px_220px_1fr]">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-reverse-type"
                label={funnelText(content, "projectionReverseGoalType", "Goal type")}
                help={funnelText(content, "projectionHelpReverseGoalType", "Choose the target metric the simulator should work backward from.")}
              />
            </span>
            <select
              aria-label={funnelText(content, "projectionReverseGoalType", "Goal type")}
              value={reverseGoalType}
              onChange={(event) => {
                const nextType = event.target.value as typeof reverseGoalType;
                setReverseGoalType(nextType);
                track("funnel_planner_reverse_goal_calculated", {
                  template_id: state.templateId ?? undefined,
                  locale,
                  goal_type: nextType,
                  feasible: reverseGoal.feasible,
                });
              }}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            >
              <option value="profit">{funnelText(content, "projectionGoalProfit", "Profit")}</option>
              <option value="revenue">{funnelText(content, "projectionGoalRevenue", "Revenue")}</option>
              <option value="leads">{funnelText(content, "projectionGoalLeads", "Leads")}</option>
              <option value="customers">{funnelText(content, "projectionGoalCustomers", "Customers")}</option>
              <option value="booked_calls">{funnelText(content, "projectionGoalBookedCalls", "Booked calls")}</option>
              <option value="cac">{funnelText(content, "projectionGoalCac", "CAC")}</option>
              <option value="payback">{funnelText(content, "projectionGoalPayback", "Payback months")}</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">
              <LabelWithHelp
                id="proj-help-reverse-target"
                label={funnelText(content, "projectionReverseGoalTarget", "Target")}
                help={funnelText(content, "projectionHelpReverseGoalTarget", "The desired value for the selected goal, such as revenue, leads, customers, CAC, or payback months.")}
              />
            </span>
            <input
              type="number"
              aria-label={funnelText(content, "projectionReverseGoalTarget", "Target")}
              min={0}
              value={reverseGoalValue}
              onChange={(event) => setReverseGoalValue(Math.max(0, Number(event.target.value)))}
              onBlur={() => {
                track("funnel_planner_reverse_goal_calculated", {
                  template_id: state.templateId ?? undefined,
                  locale,
                  goal_type: reverseGoalType,
                  feasible: reverseGoal.feasible,
                });
              }}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
          </label>
          <div className="rounded-lg border bg-muted/30 p-3 text-sm">
            <p className="font-semibold">
              {lowerIsBetterReverseGoal
                ? funnelText(content, "projectionReverseGoalCurrentValue", "Current value")
                : funnelText(content, "projectionReverseGoalResult", "Required visitors")}:{" "}
              {lowerIsBetterReverseGoal
                ? formatReverseGoalValue(reverseGoalType, reverseGoal.currentValue)
                : formatNumber(reverseGoal.requiredVisitors)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {lowerIsBetterReverseGoal
                ? funnelText(content, "projectionReverseGoalTargetGap", "Target gap")
                : funnelText(content, "projectionReverseGoalGap", "Visitor gap")}:{" "}
              {lowerIsBetterReverseGoal
                ? formatReverseGoalValue(reverseGoalType, reverseGoalGap)
                : formatNumber(reverseGoalGap)}
              {reverseGoal.requiredBudget != null ? ` · ${funnelText(content, "projectionReverseGoalBudget", "Budget")}: ${formatCurrency(reverseGoal.requiredBudget)}` : ""}
            </p>
          </div>
        </div>
      </section>

      <section role="region" aria-labelledby="proj-rules-title">
        <h3 id="proj-rules-title" className="text-lg font-bold mb-3">
          {funnelText(content, "projectionRulesTitle", "Rules-based recommendations")}
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          {recommendations.slice(0, 4).map((item) => {
            const localized = localizeRecommendation(content, state, item);
            return (
              <article key={item.id} className="rounded-lg border bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold">{localized.title}</p>
                  <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] uppercase text-slate-300">{localized.source}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{localized.detail}</p>
                <p className="mt-2 text-xs font-medium text-blue-200">{localized.action}</p>
              </article>
            );
          })}
        </div>
      </section>
      </div>
      )}

      {activeReportTab === "reports" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="reports">
      <section role="region" aria-labelledby="proj-model-health-title" data-funnel-planner-model-health>
        <div className="fp-pro-section-heading">
          <div>
            <h3 id="proj-model-health-title" className="text-lg font-bold">
              {funnelText(content, "projectionModelHealthTitle", "Model health")}
            </h3>
            <p>{funnelText(content, "projectionModelHealthHelp", "Structural checks flag graph paths that can distort the simulator before client handoff.")}</p>
          </div>
          <div className="fp-pro-summary-pill">
            {modelHealthWarnings.length === 0
              ? funnelText(content, "projectionModelHealthClear", "No structural warnings are active.")
              : funnelText(content, "projectionModelHealthWarningCount", "{count} warnings need review.", { count: modelHealthWarnings.length })}
          </div>
        </div>
        {modelHealthWarnings.length > 0 ? (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {modelHealthWarnings.slice(0, 8).map((warning) => (
              <article
                key={warning.id}
                className="rounded-lg border bg-muted/20 p-3"
                data-funnel-model-health-warning={warning.category}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold">{warning.title}</p>
                  <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] uppercase text-slate-300">
                    {warning.category.replace(/_/g, " ")}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{warning.detail}</p>
              </article>
            ))}
          </div>
        ) : null}
      </section>
      </div>
      )}

      {activeReportTab === "execution" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="execution">
      <section role="region" aria-labelledby="proj-operating-title">
        <h3 id="proj-operating-title" className="text-lg font-bold mb-3">
          {funnelText(content, "projectionOperatingModelTitle", "Operating model")}
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">{funnelText(content, "projectionRegionCurrency", "Region / currency")}</p>
            <p className="mt-1 text-sm font-semibold">{state.regionalSettings.region.toUpperCase()} · {state.regionalSettings.currency}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">{funnelText(content, "projectionTimeDelay", "Modeled delay")}</p>
            <p className="mt-1 text-sm font-semibold">{funnelText(content, "projectionDaysValue", "{count} days", { count: totalWaitDays })}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">{funnelText(content, "projectionCapacityStatus", "Capacity")}</p>
            <p className="mt-1 text-sm font-semibold">{capacity.message}</p>
          </div>
        </div>
        <div className="fp-pro-panel mt-4" data-funnel-planner-reinvestment>
          <div className="fp-pro-section-heading">
            <div>
              <h4>{funnelText(content, "projectionReinvestmentTitle", "Traffic reinvestment")}</h4>
              <p>{funnelText(content, "projectionReinvestmentHelp", "Model how much baseline projected monthly revenue is recycled into additional traffic each month.")}</p>
            </div>
            <div className="fp-pro-summary-pill" data-monthly-reinvestment-budget={modeledReinvestmentBudget}>
              {funnelText(content, "projectionModeledReinvestment", "Modeled reinvestment")}: {formatCurrency(modeledReinvestmentBudget)}
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {funnelText(content, "projectionReinvestmentPresetNote", "The selected template supplies starting reinvestment settings; edit the rate and cap to match this plan.")}
          </p>
          {usesReinvestmentCostFallback && (
            <p className="mt-2 text-xs text-muted-foreground" data-reinvestment-cost-fallback>
              {funnelText(content, "projectionCostPerVisitorFallbackNote", "No channel cost is available. Reinvestment uses {cost} per visitor from the model fallback (15% of revenue per visitor, minimum one selected-currency unit).", { cost: formatCurrency(fallbackCostPerVisitor) })}
            </p>
          )}
          <div className="fp-pro-grid fp-pro-grid--3 mt-3">
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-reinvest-percent"
                  label={funnelText(content, "projectionReinvestmentPercentField", "Reinvest %")}
                  help={funnelText(content, "projectionHelpReinvestmentPercent", "Percent of baseline projected monthly revenue recycled into additional traffic each month.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionReinvestmentPercentField", "Reinvest %")}
                min={0}
                max={100}
                step={1}
                value={reinvestmentPercent}
                onChange={(event) => updateProjectionInputs({
                  reinvestmentPercent: clampNumber(Number(event.target.value), 0, 100),
                })}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-reinvest-cap"
                  label={funnelText(content, "projectionReinvestmentCapField", "Monthly cap")}
                  help={funnelText(content, "projectionHelpReinvestmentCap", "Monthly reinvestment ceiling. Leave blank for no cap; setting it to zero stops reinvestment.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionReinvestmentCapField", "Monthly cap")}
                min={0}
                value={projectionInputs.reinvestmentCap ?? ""}
                onChange={(event) => {
                  const rawValue = event.target.value;
                  const parsedValue = Number(rawValue);
                  updateProjectionInputs({
                    reinvestmentCap: rawValue === ""
                      ? undefined
                      : Number.isFinite(parsedValue) ? Math.max(0, parsedValue) : 0,
                  });
                }}
              />
            </label>
            <div className="fp-pro-field fp-pro-field--readonly">
              <span>
                <LabelWithHelp
                  id="proj-help-reinvest-effect"
                  label={funnelText(content, "projectionReinvestmentEffectField", "Effect at month {month}", { month: horizonMonths })}
                  help={funnelText(content, "projectionHelpReinvestmentEffect", "The target projection after reinvestment assumptions are applied to the traffic model.")}
                />
              </span>
              <strong>{formatCurrency(target.points.at(-1)?.revenue ?? 0)}</strong>
            </div>
          </div>
        </div>
        <div className="fp-pro-panel mt-4" data-funnel-planner-capacity>
          <div className="fp-pro-section-heading">
            <div>
              <h4>{funnelText(content, "projectionCapacityPlannerTitle", "Capacity planner")}</h4>
              <p>{funnelText(content, "projectionCapacityPlannerHelp", "Cap sales-call throughput so the projection flags capacity bottlenecks before launch.")}</p>
            </div>
            {capacityEnabled ? (
              <button
                type="button"
                className="fp-pro-button"
                onClick={() => dispatch({ type: "SET_CAPACITY_CONFIG", capacityConfig: undefined })}
              >
                {funnelText(content, "projectionDisableCapacityButton", "Disable capacity cap")}
              </button>
            ) : (
              <button
                type="button"
                className="fp-pro-button fp-pro-button--primary"
                onClick={() => dispatch({ type: "SET_CAPACITY_CONFIG", capacityConfig: CAPACITY_DEFAULTS })}
              >
                {funnelText(content, "projectionEnableCapacityButton", "Enable capacity cap")}
              </button>
            )}
          </div>
          {capacityEnabled && (
            <div className="fp-pro-grid fp-pro-grid--5 mt-3">
              <label className="fp-pro-field">
                <span>
                  <LabelWithHelp
                    id="proj-help-capacity-max-calls"
                    label={funnelText(content, "projectionMaxCallsField", "Max calls / mo")}
                    help={funnelText(content, "projectionHelpMaxCalls", "Optional hard cap for sales or discovery calls the team can take in a month.")}
                  />
                </span>
                <input
                  type="number"
                  aria-label={funnelText(content, "projectionMaxCallsField", "Max calls / mo")}
                  min={1}
                  value={capacityConfig.maxCallsPerMonth ?? ""}
                  onChange={(event) => updateCapacity({
                    maxCallsPerMonth: event.target.value === "" ? undefined : Math.max(1, Math.round(Number(event.target.value))),
                  })}
                />
              </label>
              <label className="fp-pro-field">
                <span>
                  <LabelWithHelp
                    id="proj-help-capacity-reps"
                    label={funnelText(content, "projectionSalesRepsField", "Sales reps")}
                    help={funnelText(content, "projectionHelpSalesReps", "Number of people available to handle qualified sales calls.")}
                  />
                </span>
                <input
                  type="number"
                  aria-label={funnelText(content, "projectionSalesRepsField", "Sales reps")}
                  min={1}
                  value={capacityConfig.salesRepCount}
                  onChange={(event) => updateCapacity({ salesRepCount: Math.max(1, Math.round(Number(event.target.value))) })}
                />
              </label>
              <label className="fp-pro-field">
                <span>
                  <LabelWithHelp
                    id="proj-help-capacity-hours"
                    label={funnelText(content, "projectionRepHoursField", "Hours / rep")}
                    help={funnelText(content, "projectionHelpRepHours", "Monthly selling hours per rep after admin time and non-call work.")}
                  />
                </span>
                <input
                  type="number"
                  aria-label={funnelText(content, "projectionRepHoursField", "Hours / rep")}
                  min={1}
                  value={capacityConfig.hoursPerRepPerMonth}
                  onChange={(event) => updateCapacity({ hoursPerRepPerMonth: Math.max(1, Number(event.target.value)) })}
                />
              </label>
              <label className="fp-pro-field">
                <span>
                  <LabelWithHelp
                    id="proj-help-capacity-duration"
                    label={funnelText(content, "projectionCallDurationField", "Call mins")}
                    help={funnelText(content, "projectionHelpCallDuration", "Average length of one sales, discovery, or application call in minutes.")}
                  />
                </span>
                <input
                  type="number"
                  aria-label={funnelText(content, "projectionCallDurationField", "Call mins")}
                  min={5}
                  value={capacityConfig.avgCallDurationMins}
                  onChange={(event) => updateCapacity({ avgCallDurationMins: Math.max(5, Number(event.target.value)) })}
                />
              </label>
              <label className="fp-pro-field">
                <span>
                  <LabelWithHelp
                    id="proj-help-capacity-close"
                    label={funnelText(content, "projectionRepCloseRateField", "Rep close %")}
                    help={funnelText(content, "projectionHelpRepCloseRate", "Expected close rate after a qualified call reaches a rep.")}
                  />
                </span>
                <input
                  type="number"
                  aria-label={funnelText(content, "projectionRepCloseRateField", "Rep close %")}
                  min={0}
                  max={100}
                  value={capacityConfig.closeRateByRepPct ?? 25}
                  onChange={(event) => updateCapacity({ closeRateByRepPct: clampNumber(Number(event.target.value), 0, 100) })}
                />
              </label>
            </div>
          )}
        </div>
        <div className="fp-pro-panel mt-4" data-funnel-planner-expense-library>
          <div className="fp-pro-section-heading">
            <div>
              <h4>{funnelText(content, "projectionExpenseLibraryTitle", "Expense library")}</h4>
              <p>{funnelText(content, "projectionExpenseLibraryHelp", "Track monthly, one-time, quarterly, and annual execution costs for the P&L and reverse-goal model.")}</p>
            </div>
            <div className="fp-pro-summary-pill">
              {funnelText(content, "projectionExpenseMonthlyColumn", "Monthly total")}: {formatCurrency(monthlyExpenseTotal)}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {SEOH_EXPENSE_PRESETS.map((preset) => (
              <button
                key={preset.category}
                type="button"
                className="fp-pro-button"
                onClick={() => addExpensePreset(preset.category)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="mt-3 rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <caption className="sr-only">{funnelText(content, "projectionExpenseLibraryCaption", "Expense library by category, amount, frequency, timing, and attachment")}</caption>
              <thead>
                <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                  <th scope="col" className="text-start px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-label" label={funnelText(content, "projectionExpenseLabelColumn", "Expense")} help={funnelText(content, "projectionHelpExpenseLabel", "Internal name for the cost item shown in exports, PDF, and CRM handoff.")} />
                  </th>
                  <th scope="col" className="text-start px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-category" label={funnelText(content, "projectionExpenseCategoryColumn", "Category")} help={funnelText(content, "projectionHelpExpenseCategory", "Groups the cost into SEO/content, development, tracking, creative, compliance, sales ops, tools, labor, or general overhead.")} />
                  </th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-amount" label={funnelText(content, "projectionExpenseAmountColumn", "Amount")} help={funnelText(content, "projectionHelpExpenseAmount", "Cost amount before frequency normalization. The simulator converts it into a monthly total.")} />
                  </th>
                  <th scope="col" className="text-start px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-frequency" label={funnelText(content, "projectionExpenseFrequencyColumn", "Frequency")} help={funnelText(content, "projectionHelpExpenseFrequency", "Controls whether the amount is counted once, monthly, quarterly, or annually.")} />
                  </th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-start" label={funnelText(content, "projectionExpenseStartMonthColumn", "Start")} help={funnelText(content, "projectionHelpExpenseStartMonth", "First projection month where this cost should be included.")} />
                  </th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">
                    <LabelWithHelp id="proj-help-expense-end" label={funnelText(content, "projectionExpenseEndMonthColumn", "End")} help={funnelText(content, "projectionHelpExpenseEndMonth", "Optional last projection month for temporary launch or contractor costs.")} />
                  </th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionActionsColumn", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td className="px-3 py-3 text-sm text-muted-foreground" colSpan={7}>
                      {funnelText(content, "projectionExpenseEmpty", "No operating expenses modeled yet. Add a preset above or keep expenses at zero.")}
                    </td>
                  </tr>
                ) : expenses.map((expense) => (
                  <tr key={expense.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={expense.label}
                        className="fp-pro-table-input"
                        aria-label={funnelText(content, "projectionExpenseLabelAria", "Expense label")}
                        title={funnelText(content, "projectionHelpExpenseLabel", "Internal name for the cost item shown in exports, PDF, and CRM handoff.")}
                        onChange={(event) => updateExpense(expense.id, { label: event.target.value.slice(0, 120) || expense.label })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={expense.category}
                        className="fp-pro-table-select"
                        aria-label={funnelText(content, "projectionExpenseCategoryColumn", "Category")}
                        title={funnelText(content, "projectionHelpExpenseCategory", "Groups the cost into SEO/content, development, tracking, creative, compliance, sales ops, tools, labor, or general overhead.")}
                        onChange={(event) => updateExpense(expense.id, { category: event.target.value as FunnelExpenseCategory })}
                      >
                        {SEOH_EXPENSE_PRESETS.map((preset) => (
                          <option key={preset.category} value={preset.category}>{preset.label}</option>
                        ))}
                        <option value="general">{funnelText(content, "projectionExpenseGeneralCategory", "General")}</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        value={expense.amount}
                        className="fp-pro-table-input"
                        aria-label={funnelText(content, "projectionExpenseAmountColumn", "Amount")}
                        title={funnelText(content, "projectionHelpExpenseAmount", "Cost amount before frequency normalization. The simulator converts it into a monthly total.")}
                        onChange={(event) => updateExpense(expense.id, { amount: Math.max(0, Number(event.target.value) || 0) })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={expense.frequency}
                        className="fp-pro-table-select"
                        aria-label={funnelText(content, "projectionExpenseFrequencyColumn", "Frequency")}
                        title={funnelText(content, "projectionHelpExpenseFrequency", "Controls whether the amount is counted once, monthly, quarterly, or annually.")}
                        onChange={(event) => updateExpense(expense.id, { frequency: event.target.value as FunnelWizardState["expenses"][number]["frequency"] })}
                      >
                        <option value="one_time">{funnelText(content, "projectionExpenseFrequencyOneTime", "One-time")}</option>
                        <option value="monthly">{funnelText(content, "projectionExpenseFrequencyMonthly", "Monthly")}</option>
                        <option value="quarterly">{funnelText(content, "projectionExpenseFrequencyQuarterly", "Quarterly")}</option>
                        <option value="annual">{funnelText(content, "projectionExpenseFrequencyAnnual", "Annual")}</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={1}
                        max={60}
                        value={expense.startMonth ?? 1}
                        className="fp-pro-table-input"
                        aria-label={funnelText(content, "projectionExpenseStartMonthColumn", "Start")}
                        title={funnelText(content, "projectionHelpExpenseStartMonth", "First projection month where this cost should be included.")}
                        onChange={(event) => updateExpense(expense.id, { startMonth: clampNumber(Math.round(Number(event.target.value)), 1, 60) })}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={1}
                        max={60}
                        value={expense.endMonth ?? ""}
                        className="fp-pro-table-input"
                        aria-label={funnelText(content, "projectionExpenseEndMonthColumn", "End")}
                        title={funnelText(content, "projectionHelpExpenseEndMonth", "Optional last projection month for temporary launch or contractor costs.")}
                        onChange={(event) => updateExpense(expense.id, {
                          endMonth: event.target.value === "" ? undefined : clampNumber(Math.round(Number(event.target.value)), 1, 60),
                        })}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="fp-pro-link-button" onClick={() => removeExpense(expense.id)}>
                        {funnelText(content, "projectionRemoveExpenseButton", "Remove")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      </div>
      )}

      {activeReportTab === "actuals" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="actuals">
      <section role="region" aria-labelledby="proj-actual-plan-title" data-funnel-planner-actual-dashboard>
        <div className="fp-pro-section-heading">
          <div>
            <h3 id="proj-actual-plan-title" className="text-lg font-bold">
              {funnelText(content, "projectionActualPlanTitle", "Plan vs actual")}
            </h3>
            <p>{funnelText(content, "projectionActualPlanHelp", "Enter real stage volume and CVR to compare execution against the forecast.")}</p>
          </div>
        </div>
        <div
          role="status"
          className="mb-3 rounded-lg border px-3 py-2 text-sm"
          style={{
            borderColor: actualBehindCount > 0 ? "#ef4444" : actualAttentionCount > 0 || actualMissingCount > 0 ? "#f59e0b" : "#22c55e",
            color: actualBehindCount > 0 ? "#fecaca" : actualAttentionCount > 0 || actualMissingCount > 0 ? "#fde68a" : "#bbf7d0",
            background: actualBehindCount > 0
              ? "rgba(127,29,29,0.24)"
              : actualAttentionCount > 0 || actualMissingCount > 0
                ? "rgba(120,53,15,0.22)"
                : "rgba(20,83,45,0.20)",
          }}
        >
          {actualBehindCount > 0
            ? funnelText(content, "projectionActualPlanBehind", "{count} stages are behind plan and need review.", { count: actualBehindCount })
            : actualAttentionCount > 0
              ? funnelText(content, "projectionActualPlanAttention", "{count} actual rows need period review.", { count: actualAttentionCount })
            : actualMissingCount > 0
              ? funnelText(content, "projectionActualPlanMissing", "{count} stages still need actuals before drift can be judged.", { count: actualMissingCount })
              : funnelText(content, "projectionActualPlanHealthy", "No material drift detected in stages with actuals.")}
        </div>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[1040px] text-sm">
            <caption className="sr-only">{funnelText(content, "projectionActualPlanCaption", "Planned and actual stage conversion and volume comparison")}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionStageColumn", "Stage")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionPlannedVolumeColumn", "Planned volume")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-volume" label={funnelText(content, "projectionActualVolumeColumn", "Actual volume")} help={funnelText(content, "projectionHelpActualVolume", "Measured stage volume from the selected actuals period before monthly normalization.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-normalized" label={funnelText(content, "projectionActualNormalizedVolumeColumn", "Normalized monthly actual")} help={funnelText(content, "projectionHelpActualNormalizedVolume", "Actual volume scaled to a 30-day period so partial periods compare fairly with the forecast.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-period-days" label={funnelText(content, "projectionActualPeriodDaysColumn", "Actual period days")} help={funnelText(content, "projectionHelpActualPeriodDays", "Number of days covered by the actuals you entered for this stage.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-volume-drift" label={funnelText(content, "projectionActualVolumeDeltaColumn", "Volume drift")} help={funnelText(content, "projectionHelpActualDrift", "Difference between forecast and normalized actuals after period adjustment.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionPlannedCvrColumn", "Planned CVR")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-cvr" label={funnelText(content, "projectionActualCvrColumn", "Actual CVR")} help={funnelText(content, "projectionHelpActualCvr", "Measured conversion rate for this stage during the actuals period.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-actual-cvr-drift" label={funnelText(content, "projectionActualCvrDeltaColumn", "CVR drift")} help={funnelText(content, "projectionHelpActualDrift", "Difference between forecast and normalized actuals after period adjustment.")} />
                </th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionStatusColumn", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {actualRows.map((row) => (
                <tr key={row.stageId} className="border-b last:border-0" data-funnel-actual-row={row.stageId}>
                  <td className="px-3 py-2 font-medium">{row.stageName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(Math.round(row.plannedVolume))}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min={0}
                      value={row.actualVolume ?? ""}
                      aria-label={funnelText(content, "projectionActualVolumeAria", "Actual volume for {stage}", { stage: row.stageName })}
                      className="fp-pro-table-input"
                      title={funnelText(content, "projectionHelpActualVolume", "Measured stage volume from the selected actuals period before monthly normalization.")}
                      onChange={(event) => {
                        const value = event.target.value === "" ? undefined : Math.max(0, Math.round(Number(event.target.value)));
                        updateStage(row.stageId, { actualVolume: Number.isFinite(value) ? value : undefined });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.normalizedActualVolume == null ? notAvailable : formatNumber(Math.round(row.normalizedActualVolume))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min={1}
                      max={366}
                      value={row.actualPeriodDays}
                      aria-label={funnelText(content, "projectionActualPeriodDaysAria", "Actual period days for {stage}", { stage: row.stageName })}
                      className="fp-pro-table-input"
                      title={funnelText(content, "projectionHelpActualPeriodDays", "Number of days covered by the actuals you entered for this stage.")}
                      onChange={(event) => {
                        const value = clampNumber(Math.round(Number(event.target.value)), 1, 366);
                        updateStage(row.stageId, { actualPeriodDays: value });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" style={actualStatusStyle(row.status)}>
                    {formatSignedNumber(row.volumeDelta)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatPct(row.plannedCvr)}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={row.actualCvr ?? ""}
                      aria-label={funnelText(content, "projectionActualCvrAria", "Actual CVR for {stage}", { stage: row.stageName })}
                      className="fp-pro-table-input"
                      title={funnelText(content, "projectionHelpActualCvr", "Measured conversion rate for this stage during the actuals period.")}
                      onChange={(event) => {
                        const value = event.target.value === "" ? undefined : clampNumber(Number(event.target.value), 0, 100);
                        updateStage(row.stageId, { actualCvr: Number.isFinite(value) ? value : undefined });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" style={actualStatusStyle(row.status)}>
                    {row.cvrDelta == null ? notAvailable : `${formatSignedNumber(row.cvrDelta, 1)} pp`}
                  </td>
                  <td className="px-3 py-2 text-right text-xs uppercase" style={actualStatusStyle(row.status)}>
                    {actualStatusLabel(row.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section role="region" aria-labelledby="proj-tracking-plan-title" data-funnel-planner-tracking-plan>
        <div className="fp-pro-section-heading">
          <div>
            <h3 id="proj-tracking-plan-title" className="text-lg font-bold">
              {funnelText(content, "projectionTrackingPlanTitle", "Per-node tracking plan")}
            </h3>
            <p>{funnelText(content, "projectionTrackingPlanHelp", "Use these generated event names and fields to instrument each funnel node in GTM and PostHog.")}</p>
          </div>
        </div>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <caption className="sr-only">{funnelText(content, "projectionTrackingPlanCaption", "Generated tracking plan by stage, event name, GTM fields, PostHog fields, conversion definition, attribution role, and tracking status")}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionStageColumn", "Stage")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingEventColumn", "Event name")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingTriggerColumn", "Trigger")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingGtmFieldsColumn", "GTM fields")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingPosthogFieldsColumn", "PostHog fields")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingConversionColumn", "Conversion definition")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionTrackingAttributionColumn", "Attribution")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-tracking-status" label={funnelText(content, "projectionStatusColumn", "Status")} help={funnelText(content, "projectionHelpTrackingStatus", "Shows how instrumented the stage is, from no tracking through verified CRM-matched measurement.")} />
                </th>
              </tr>
            </thead>
            <tbody>
              {trackingPlanRows.map((row) => (
                <tr key={row.stageId} className="border-b last:border-0" data-funnel-tracking-plan-row={row.stageId}>
                  <td className="px-3 py-2 font-medium">{row.stageName}</td>
                  <td className="px-3 py-2">
                    <code className="rounded bg-muted px-2 py-1 text-xs">{row.eventName}</code>
                  </td>
                  <td className="px-3 py-2">{trackingTriggerLabel(row.trigger)}</td>
                  <td className="px-3 py-2 text-xs">{row.gtmFields.join(", ")}</td>
                  <td className="px-3 py-2 text-xs">{row.posthogFields.join(", ")}</td>
                  <td className="px-3 py-2">
                    {funnelText(content, "projectionTrackingConversionDefinition", "Count this node when it produces {metric}.", {
                      metric: outputMetricLabel(row.outputMetric),
                    })}
                  </td>
                  <td className="px-3 py-2">{attributionLabel(row.attributionRole)}</td>
                  <td className="px-3 py-2 text-right">
                    <select
                      value={row.trackingStatus}
                      aria-label={`${trackingStatusLabel(row.trackingStatus)} - ${row.stageName}`}
                      className="fp-pro-table-select"
                      title={funnelText(content, "projectionHelpTrackingStatus", "Shows how instrumented the stage is, from no tracking through verified CRM-matched measurement.")}
                      onChange={(event) => updateStage(row.stageId, {
                        trackingStatus: event.target.value as NonNullable<FunnelWizardState["stages"][number]["trackingStatus"]>,
                      })}
                    >
                      {TRACKING_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {funnelText(content, option.key, option.fallback)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}

      {activeReportTab === "execution" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="execution-workspace">
      <section role="region" aria-labelledby="proj-delivery-title" data-funnel-planner-delivery-roadmap>
        <h3 id="proj-delivery-title" className="text-lg font-bold mb-3">
          {funnelText(content, "projectionDeliveryTitle", "Client delivery roadmap")}
        </h3>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">{funnelText(content, "projectionDeliveryCaption", "Execution checklist by stage owner, deadline, asset status, and priority")}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionDeliveryStageColumn", "Stage")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionDeliveryOwnerColumn", "Owner")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionDeliveryDueColumn", "Due")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionDeliveryStatusColumn", "Asset status")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionDeliveryPriorityColumn", "Priority")}</th>
              </tr>
            </thead>
            <tbody>
              {deliveryRows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-2">{row.name}</td>
                  <td className="px-3 py-2">{row.owner}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{funnelText(content, "projectionDaysValue", "{count} days", { count: row.dueInDays })}</td>
                  <td className="px-3 py-2 text-right text-xs uppercase">{String(row.assetStatus).replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-right text-xs uppercase" style={{ color: row.priority === "high" ? "#fbbf24" : "#94a3b8" }}>
                    {row.priority}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section role="region" aria-labelledby="proj-asset-inventory-title" data-funnel-planner-asset-inventory>
        <div className="fp-pro-section-heading">
          <div>
            <h3 id="proj-asset-inventory-title" className="text-lg font-bold">
              {funnelText(content, "projectionAssetInventoryTitle", "Asset inventory")}
            </h3>
            <p>{funnelText(content, "projectionAssetInventoryHelp", "Track the execution asset for each stage, including source system, owner, status, tracking, and CTA notes.")}</p>
          </div>
        </div>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[1200px] text-sm">
            <caption className="sr-only">{funnelText(content, "projectionAssetInventoryCaption", "Asset inventory by stage, URL, source, type, owner, status, tracking, and notes")}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionStageColumn", "Stage")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-url" label={funnelText(content, "projectionAssetUrlColumn", "Asset URL")} help={funnelText(content, "projectionHelpAssetUrl", "Link to the live, draft, or source asset used for this stage. It is stored in CRM and exports, not sent to PostHog.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-type" label={funnelText(content, "projectionAssetTypeColumn", "Asset type")} help={funnelText(content, "projectionHelpAssetType", "Classifies the deliverable, such as page, video, email, ad, form, checkout, or report.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-source" label={funnelText(content, "projectionAssetSourceColumn", "Source system")} help={funnelText(content, "projectionHelpAssetSource", "Where the asset is managed or measured, such as CMS, CRM, GTM, PostHog, client site, or SEOH.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-owner" label={funnelText(content, "projectionDeliveryOwnerColumn", "Owner")} help={funnelText(content, "projectionHelpAssetOwner", "Person or team responsible for building, publishing, or maintaining this stage asset.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-status" label={funnelText(content, "projectionStatusColumn", "Status")} help={funnelText(content, "projectionHelpAssetStatus", "Delivery state for the asset, from unassigned through measured or needs fix.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-tracking" label={funnelText(content, "projectionTrackingStatusColumn", "Tracking")} help={funnelText(content, "projectionHelpTrackingStatus", "Shows how instrumented the stage is, from no tracking through verified CRM-matched measurement.")} />
                </th>
                <th scope="col" className="text-start px-3 py-2 font-medium">
                  <LabelWithHelp id="proj-help-asset-notes" label={funnelText(content, "projectionAssetNotesColumn", "CTA / output notes")} help={funnelText(content, "projectionHelpAssetNotes", "Notes for interactive-video CTA behavior, output expectations, or handoff details.")} />
                </th>
              </tr>
            </thead>
            <tbody>
              {assetInventory.map((row) => (
                <tr key={row.stageId} className="border-b last:border-0" data-funnel-asset-row={row.stageId}>
                  <td className="px-3 py-2 font-medium">{row.stageName}</td>
                  <td className="px-3 py-2">
                    <input
                      type="url"
                      value={row.assetUrl ?? ""}
                      className="fp-pro-table-input"
                      aria-label={funnelText(content, "projectionAssetUrlAria", "Asset URL for {stage}", { stage: row.stageName })}
                      title={funnelText(content, "projectionHelpAssetUrl", "Link to the live, draft, or source asset used for this stage. It is stored in CRM and exports, not sent to PostHog.")}
                      onChange={(event) => updateStage(row.stageId, { assetUrl: event.target.value.slice(0, 500) || undefined })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.assetType ?? "other"}
                      className="fp-pro-table-select"
                      aria-label={funnelText(content, "projectionAssetTypeColumn", "Asset type")}
                      title={funnelText(content, "projectionHelpAssetType", "Classifies the deliverable, such as page, video, email, ad, form, checkout, or report.")}
                      onChange={(event) => updateStage(row.stageId, { assetType: event.target.value as FunnelWizardState["stages"][number]["assetType"] })}
                    >
                      {FUNNEL_ASSET_TYPES.map((type) => (
                        <option key={type} value={type}>{type.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.sourceSystem ?? "other"}
                      className="fp-pro-table-select"
                      aria-label={funnelText(content, "projectionAssetSourceColumn", "Source system")}
                      title={funnelText(content, "projectionHelpAssetSource", "Where the asset is managed or measured, such as CMS, CRM, GTM, PostHog, client site, or SEOH.")}
                      onChange={(event) => updateStage(row.stageId, { assetSourceSystem: event.target.value as FunnelWizardState["stages"][number]["assetSourceSystem"] })}
                    >
                      {FUNNEL_ASSET_SOURCE_SYSTEMS.map((source) => (
                        <option key={source} value={source}>{source.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.owner ?? ""}
                      className="fp-pro-table-input"
                      aria-label={funnelText(content, "projectionDeliveryOwnerColumn", "Owner")}
                      title={funnelText(content, "projectionHelpAssetOwner", "Person or team responsible for building, publishing, or maintaining this stage asset.")}
                      onChange={(event) => updateStage(row.stageId, { owner: event.target.value.slice(0, 80) || undefined })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.status ?? "unassigned"}
                      className="fp-pro-table-select"
                      aria-label={funnelText(content, "projectionStatusColumn", "Status")}
                      title={funnelText(content, "projectionHelpAssetStatus", "Delivery state for the asset, from unassigned through measured or needs fix.")}
                      onChange={(event) => updateStage(row.stageId, {
                        assetExecutionStatus: event.target.value as FunnelWizardState["stages"][number]["assetExecutionStatus"],
                      })}
                    >
                      {FUNNEL_ASSET_EXECUTION_STATUSES.map((status) => (
                        <option key={status} value={status}>{status.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.trackingStatus}
                      aria-label={`${trackingStatusLabel(row.trackingStatus)} - ${row.stageName}`}
                      className="fp-pro-table-select"
                      title={funnelText(content, "projectionHelpTrackingStatus", "Shows how instrumented the stage is, from no tracking through verified CRM-matched measurement.")}
                      onChange={(event) => updateStage(row.stageId, {
                        trackingStatus: event.target.value as NonNullable<FunnelWizardState["stages"][number]["trackingStatus"]>,
                      })}
                    >
                      {TRACKING_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {funnelText(content, option.key, option.fallback)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.ctaNotes ?? ""}
                      className="fp-pro-table-input"
                      aria-label={funnelText(content, "projectionAssetNotesAria", "CTA or output notes for {stage}", { stage: row.stageName })}
                      title={funnelText(content, "projectionHelpAssetNotes", "Notes for interactive-video CTA behavior, output expectations, or handoff details.")}
                      onChange={(event) => updateStage(row.stageId, { assetCtaNotes: event.target.value.slice(0, 280) || undefined })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section role="region" aria-labelledby="proj-experiments-title" data-funnel-planner-experiments>
        <h3 id="proj-experiments-title" className="text-lg font-bold mb-3">
          {funnelText(content, "projectionExperimentsTitle", "Experiment plan")}
        </h3>
        <div className="fp-pro-panel">
          <div className="fp-pro-section-heading">
            <div>
              <h4>{funnelText(content, "projectionExperimentBuilderTitle", "A/B test builder")}</h4>
              <p>{funnelText(content, "projectionExperimentBuilderHelp", "Attach tests to funnel stages and track split, KPI, winner, and actual uplift.")}</p>
            </div>
            <button
              type="button"
              className="fp-pro-button fp-pro-button--primary"
              onClick={handleAddExperiment}
              disabled={state.experiments.length >= 12 || experimentDraft.hypothesis.trim().length === 0 || !experimentStageId}
            >
              {funnelText(content, "projectionAddExperimentButton", "Add experiment")}
            </button>
          </div>
          <div className="fp-pro-grid fp-pro-grid--4 mt-3">
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-stage"
                  label={funnelText(content, "projectionExperimentStageField", "Stage")}
                  help={funnelText(content, "projectionHelpExperimentStage", "Stage where the experiment runs and where uplift should be measured.")}
                />
              </span>
              <select
                aria-label={funnelText(content, "projectionExperimentStageField", "Stage")}
                value={experimentStageId}
                title={funnelText(content, "projectionHelpExperimentStage", "Stage where the experiment runs and where uplift should be measured.")}
                onChange={(event) => setExperimentDraft((current) => ({ ...current, stageId: event.target.value }))}
              >
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </select>
            </label>
            <label className="fp-pro-field fp-pro-field--wide">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-hypothesis"
                  label={funnelText(content, "projectionExperimentHypothesisField", "Hypothesis")}
                  help={funnelText(content, "projectionHelpExperimentHypothesis", "Plain-language statement of what change should improve the selected KPI.")}
                />
              </span>
              <input
                type="text"
                aria-label={funnelText(content, "projectionExperimentHypothesisField", "Hypothesis")}
                value={experimentDraft.hypothesis}
                title={funnelText(content, "projectionHelpExperimentHypothesis", "Plain-language statement of what change should improve the selected KPI.")}
                onChange={(event) => setExperimentDraft((current) => ({ ...current, hypothesis: event.target.value.slice(0, 240) }))}
                placeholder={funnelText(content, "projectionExperimentHypothesisPlaceholder", "Changing the proof block will lift opt-ins.")}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-kpi"
                  label={funnelText(content, "projectionExperimentPrimaryKpiField", "Primary KPI")}
                  help={funnelText(content, "projectionHelpExperimentPrimaryKpi", "Main metric used to decide whether the experiment wins.")}
                />
              </span>
              <select
                aria-label={funnelText(content, "projectionExperimentPrimaryKpiField", "Primary KPI")}
                value={experimentDraft.primaryKpi}
                title={funnelText(content, "projectionHelpExperimentPrimaryKpi", "Main metric used to decide whether the experiment wins.")}
                onChange={(event) => setExperimentDraft((current) => ({
                  ...current,
                  primaryKpi: event.target.value as ExperimentDraft["primaryKpi"],
                }))}
              >
                <option value="cvr">CVR</option>
                <option value="revenue">{funnelText(content, "projectionGoalRevenue", "Revenue")}</option>
                <option value="leads">{funnelText(content, "projectionGoalLeads", "Leads")}</option>
                <option value="customers">{funnelText(content, "projectionGoalCustomers", "Customers")}</option>
                <option value="booked_calls">{funnelText(content, "projectionGoalBookedCalls", "Booked calls")}</option>
                <option value="cac">{funnelText(content, "projectionGoalCac", "CAC")}</option>
                <option value="aov">AOV</option>
              </select>
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-split"
                  label={funnelText(content, "projectionExperimentSplitField", "Traffic split %")}
                  help={funnelText(content, "projectionHelpExperimentSplit", "Percent of traffic assigned to the test variant for planning and handoff.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionExperimentSplitField", "Traffic split %")}
                min={0}
                max={100}
                value={experimentDraft.trafficSplitPct}
                title={funnelText(content, "projectionHelpExperimentSplit", "Percent of traffic assigned to the test variant for planning and handoff.")}
                onChange={(event) => setExperimentDraft((current) => ({
                  ...current,
                  trafficSplitPct: clampNumber(Number(event.target.value), 0, 100),
                }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-variant-a"
                  label={funnelText(content, "projectionExperimentVariantAField", "Variant A")}
                  help={funnelText(content, "projectionHelpExperimentVariantA", "Control version name or short description.")}
                />
              </span>
              <input
                type="text"
                aria-label={funnelText(content, "projectionExperimentVariantAField", "Variant A")}
                value={experimentDraft.variantA}
                title={funnelText(content, "projectionHelpExperimentVariantA", "Control version name or short description.")}
                onChange={(event) => setExperimentDraft((current) => ({ ...current, variantA: event.target.value.slice(0, 120) }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-variant-b"
                  label={funnelText(content, "projectionExperimentVariantBField", "Variant B")}
                  help={funnelText(content, "projectionHelpExperimentVariantB", "Test version name or short description.")}
                />
              </span>
              <input
                type="text"
                aria-label={funnelText(content, "projectionExperimentVariantBField", "Variant B")}
                value={experimentDraft.variantB}
                title={funnelText(content, "projectionHelpExperimentVariantB", "Test version name or short description.")}
                onChange={(event) => setExperimentDraft((current) => ({ ...current, variantB: event.target.value.slice(0, 120) }))}
              />
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-status"
                  label={funnelText(content, "projectionExperimentStatusField", "Status")}
                  help={funnelText(content, "projectionHelpExperimentStatus", "Current test state for execution reporting.")}
                />
              </span>
              <select
                aria-label={funnelText(content, "projectionExperimentStatusField", "Status")}
                value={experimentDraft.status}
                title={funnelText(content, "projectionHelpExperimentStatus", "Current test state for execution reporting.")}
                onChange={(event) => setExperimentDraft((current) => ({
                  ...current,
                  status: event.target.value as ExperimentDraft["status"],
                }))}
              >
                <option value="idea">{funnelText(content, "projectionExperimentStatusIdea", "Idea")}</option>
                <option value="running">{funnelText(content, "projectionExperimentStatusRunning", "Running")}</option>
                <option value="paused">{funnelText(content, "projectionExperimentStatusPaused", "Paused")}</option>
                <option value="winner">{funnelText(content, "projectionExperimentStatusWinner", "Winner")}</option>
                <option value="lost">{funnelText(content, "projectionExperimentStatusLost", "Lost")}</option>
              </select>
            </label>
            <label className="fp-pro-field">
              <span>
                <LabelWithHelp
                  id="proj-help-experiment-uplift"
                  label={funnelText(content, "projectionExperimentUpliftField", "Actual uplift %")}
                  help={funnelText(content, "projectionHelpExperimentUplift", "Measured lift after the test has enough data; blank means not measured yet.")}
                />
              </span>
              <input
                type="number"
                aria-label={funnelText(content, "projectionExperimentUpliftField", "Actual uplift %")}
                min={-100}
                max={500}
                value={experimentDraft.actualUpliftPct ?? ""}
                title={funnelText(content, "projectionHelpExperimentUplift", "Measured lift after the test has enough data; blank means not measured yet.")}
                onChange={(event) => setExperimentDraft((current) => ({
                  ...current,
                  actualUpliftPct: event.target.value === "" ? undefined : clampNumber(Number(event.target.value), -100, 500),
                }))}
              />
            </label>
          </div>
        </div>
        <div className="mt-4 rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">{funnelText(content, "projectionExperimentsCaption", "Experiment plan by stage, KPI, status, winner, and actual uplift")}</caption>
            <thead>
              <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionExperimentStageField", "Stage")}</th>
                <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionExperimentHypothesisField", "Hypothesis")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionExperimentPrimaryKpiField", "Primary KPI")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionExperimentStatusField", "Status")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionExperimentWinnerField", "Winner")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionExperimentUpliftField", "Actual uplift %")}</th>
                <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionActionsColumn", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {state.experiments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                    {funnelText(content, "projectionExperimentsEmpty", "No experiments planned yet. Add one above.")}
                  </td>
                </tr>
              ) : state.experiments.map((experiment) => {
                const stageName = stages.find((stage) => stage.id === experiment.stageId)?.name ?? experiment.stageId;
                return (
                  <tr key={experiment.id} className="border-b last:border-0">
                    <td className="px-3 py-2">{stageName}</td>
                    <td className="px-3 py-2">{experiment.hypothesis}</td>
                    <td className="px-3 py-2 text-right uppercase">{experiment.primaryKpi.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-right">
                      <select
                        value={experiment.status}
                        title={funnelText(content, "projectionHelpExperimentStatus", "Current test state for execution reporting.")}
                        onChange={(event) => updateExperiment(experiment.id, {
                          status: event.target.value as FunnelWizardState["experiments"][number]["status"],
                        })}
                        className="fp-pro-table-select"
                      >
                        <option value="idea">{funnelText(content, "projectionExperimentStatusIdea", "Idea")}</option>
                        <option value="running">{funnelText(content, "projectionExperimentStatusRunning", "Running")}</option>
                        <option value="paused">{funnelText(content, "projectionExperimentStatusPaused", "Paused")}</option>
                        <option value="winner">{funnelText(content, "projectionExperimentStatusWinner", "Winner")}</option>
                        <option value="lost">{funnelText(content, "projectionExperimentStatusLost", "Lost")}</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <select
                        value={experiment.winner}
                        title={funnelText(content, "projectionHelpExperimentWinner", "Winning variant once the experiment has a decision, or none while undecided.")}
                        onChange={(event) => updateExperiment(experiment.id, {
                          winner: event.target.value as FunnelWizardState["experiments"][number]["winner"],
                        })}
                        className="fp-pro-table-select"
                      >
                        <option value="none">{funnelText(content, "projectionExperimentWinnerNone", "None")}</option>
                        <option value="a">A</option>
                        <option value="b">B</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <input
                        type="number"
                        min={-100}
                        max={500}
                        value={experiment.actualUpliftPct ?? ""}
                        title={funnelText(content, "projectionHelpExperimentUplift", "Measured lift after the test has enough data; blank means not measured yet.")}
                        onChange={(event) => updateExperiment(experiment.id, {
                          actualUpliftPct: event.target.value === "" ? undefined : clampNumber(Number(event.target.value), -100, 500),
                        })}
                        className="fp-pro-table-input"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="fp-pro-link-button" onClick={() => removeExperiment(experiment.id)}>
                        {funnelText(content, "projectionRemoveExperimentButton", "Remove")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}

      {activeReportTab === "projection" && (
      <div className="space-y-8" data-funnel-planner-tab-panel="projection-financials">

      {/* SECTION 2: P&L Table */}
      <section role="region" aria-labelledby={headingRefs.pnl}>
        <h3 id={headingRefs.pnl} className="text-lg font-bold mb-3">
          {funnelText(content, "projectionPnlTitle", "Revenue & P&L Breakdown")}
        </h3>
        {!hasProducts ? (
          <div
            role="status"
            style={{
              borderRadius: 8,
              background: "rgba(30,58,138,0.20)",
              border: "1px solid #1e40af",
              padding: "12px 16px",
              fontSize: 14,
              color: "#93c5fd",
            }}
          >
            {funnelText(content, "projectionPnlLocked", "Add products in step 3 to unlock P&L breakdown.")}
          </div>
        ) : pnl ? (
          <div className="space-y-4">
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <caption className="sr-only">{funnelText(content, "projectionPnlCaption", "Revenue and profit and loss breakdown")}</caption>
                <tbody>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionGrossRevenue", "Gross Revenue")}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(pnl.grossRevenue)}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionRefunds", "Refunds")}</td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: "#ef4444" }}>-{formatCurrency(pnl.refundAmount)}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionMerchantFees", "Merchant Fees")}</td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: "#ef4444" }}>-{formatCurrency(pnl.merchantFees)}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionCogs", "COGS")}</td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: "#ef4444" }}>-{formatCurrency(pnl.cogsAmount)}</td>
                </tr>
                <tr className="border-b bg-muted/20">
                  <td className="px-4 py-2 font-semibold">{funnelText(content, "projectionNetRevenue", "Net Revenue")}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{formatCurrency(pnl.netRevenue)}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionTrafficCost", "Traffic Cost")}</td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: "#ef4444" }}>-{formatCurrency(pnl.totalTrafficCost)}</td>
                </tr>
                <tr className="border-b bg-muted/20" style={pnl.grossProfit < 0 ? { borderLeft: "3px solid #ef4444" } : { borderLeft: "3px solid #22c55e" }}>
                  <td className="px-4 py-2 font-semibold">{funnelText(content, "projectionGrossProfit", "Gross Profit")}</td>
                  <td
                    className="px-4 py-2 text-right tabular-nums font-semibold"
                    style={{ color: pnl.grossProfit >= 0 ? "#22c55e" : "#ef4444" }}
                    aria-label={funnelText(
                      content,
                      pnl.grossProfit >= 0 ? "projectionPositiveMetricAria" : "projectionNegativeMetricAria",
                      "{metric}: {value}",
                      {
                        metric: funnelText(content, "projectionGrossProfit", "Gross Profit"),
                        value: formatCurrency(Math.abs(pnl.grossProfit)),
                      },
                    )}
                  >
                    {pnl.grossProfit >= 0 ? "+" : ""}{formatCurrency(pnl.grossProfit)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 text-muted-foreground">
                    {funnelText(content, "projectionOperatingExpenses", "Operating Expenses")}
                    <span className="ms-1 text-xs text-muted-foreground">
                      ({funnelText(content, "projectionMonthlyUsd", "monthly USD").replace(/\bUSD\b/g, state.regionalSettings.currency)})
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: "#ef4444" }}>
                    -{formatCurrency(pnl.operatingExpenses)}
                  </td>
                </tr>
                <tr
                  className="border-b bg-muted/30"
                  style={pnl.netProfit < 0 ? { borderLeft: "3px solid #ef4444" } : { borderLeft: "3px solid #22c55e" }}
                >
                  <td className="px-4 py-2 text-base font-bold">{funnelText(content, "projectionNetProfit", "Net Profit")}</td>
                  <td
                    className="px-4 py-2 text-right tabular-nums text-base font-bold"
                    style={{ color: pnl.netProfit >= 0 ? "#22c55e" : "#ef4444" }}
                    aria-label={funnelText(
                      content,
                      pnl.netProfit >= 0 ? "projectionPositiveMetricAria" : "projectionNegativeMetricAria",
                      "{metric}: {value}",
                      {
                        metric: funnelText(content, "projectionNetProfit", "Net Profit"),
                        value: formatCurrency(Math.abs(pnl.netProfit)),
                      },
                    )}
                  >
                    {pnl.netProfit >= 0 ? "+" : ""}{formatCurrency(pnl.netProfit)}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-muted-foreground">{funnelText(content, "projectionProfitMargin", "Profit Margin")}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatPct(pnl.profitMarginPct)}</td>
                </tr>
                </tbody>
              </table>
            </div>
            {productRevenueRows.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold">
                  {funnelText(content, "projectionProductRevenueTitle", "Product-level revenue")}
                </h4>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <caption className="sr-only">{funnelText(content, "projectionProductRevenueCaption", "Product revenue and margin by offer role")}</caption>
                    <thead>
                      <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                        <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionProductColumn", "Product")}</th>
                        <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionRoleColumn", "Role")}</th>
                        <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionUnitsColumn", "Units")}</th>
                        <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionGrossRevenue", "Gross Revenue")}</th>
                        <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionNetRevenueColumn", "Net revenue")}</th>
                        <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionMarginColumn", "Margin")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productRevenueRows.map((row) => (
                        <tr key={row.productId} className="border-b last:border-0">
                          <td className="px-3 py-2">{row.productName}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{(row.role ?? "core").replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.units)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.grossRevenue)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.netRevenue)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatPct(row.marginPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </section>

      {/* SECTION 3: KPI Cards */}
      <section
        role="region"
        aria-labelledby={headingRefs.kpi}
        data-funnel-planner-projection-kpis="true"
        data-audit-ticket={PROJECTION_KPI_AUDIT_TICKET}
        data-tier-one-family="offer_assembly_configurator_pricing"
        data-gamification-current-state={projectionKpiReady ? "complete_result" : "active_inprogress"}
        data-gamification-reduced-motion-static="supported"
        data-gamification-supported-states={READINESS_SUPPORTED_STATES}
        data-readiness-ready-count={projectionKpiReady ? 1 : 0}
        data-readiness-total-count={1}
        data-readiness-tier={projectionKpiReady ? "projection_kpis_ready" : "projection_kpis_waiting"}
        data-blockers-bucket={projectionKpiReady ? "none" : "one"}
        data-gamification-count-up-real-numbers="true"
        data-gamification-real-number-source="horizon_revenue"
        data-gamification-real-number-count={projectionKpiRealNumberCount}
        data-gamification-real-number-total={Math.max(1, projectionKpiRealNumberCount)}
      >
        <span className="sr-only" role="status" aria-live="polite">
          {funnelText(content, "gamificationProjectionKpisReady", "Projection KPI real-number count-up ready")}
        </span>
        <h3 id={headingRefs.kpi} className="text-base font-semibold mb-3">
          {funnelText(content, "projectionKpiTitle", "Key Performance Indicators")}
        </h3>
        {hasProducts && kpis ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-roas-label">
              <p id="kpi-roas-label" className="text-xs text-muted-foreground uppercase tracking-wide">ROAS</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">
                {trafficCostTotal > 0 && kpis.roas != null ? kpis.roas.toFixed(2) + "x" : <span title={roasTooltip}>{notAvailable}</span>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionKpiTargetRoas", "Target: 3x+")}</p>
            </div>
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-cpa-label">
              <p id="kpi-cpa-label" className="text-xs text-muted-foreground uppercase tracking-wide">CPA</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{kpis.cpa != null ? formatCurrency(kpis.cpa) : <span title={requiresTrafficCostTitle}>{notAvailable}</span>}</p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionCostPerAcquisition", "Cost per acquisition")}</p>
            </div>
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-ltv-label">
              <p id="kpi-ltv-label" className="text-xs text-muted-foreground uppercase tracking-wide">LTV</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{kpis.ltv != null ? formatCurrency(kpis.ltv) : <span title={requiresTrafficCostTitle}>{notAvailable}</span>}</p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionLifetimeValue", "Lifetime value")}</p>
            </div>
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-cac-label">
              <p id="kpi-cac-label" className="text-xs text-muted-foreground uppercase tracking-wide">{funnelText(content, "projectionCacPayback", "CAC Payback")}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">
                {kpis.cacPaybackMonths != null && isFinite(kpis.cacPaybackMonths)
                  ? funnelText(content, "projectionMonthsValue", "{count} mo", { count: kpis.cacPaybackMonths.toFixed(1) })
                  : <span title={requiresTrafficCostTitle}>{notAvailable}</span>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionCacPaybackHelp", "Months to recover CAC")}</p>
            </div>
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-bev-label">
              <p id="kpi-bev-label" className="text-xs text-muted-foreground uppercase tracking-wide">{funnelText(content, "projectionBreakEvenVisitors", "Break-Even Visitors")}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">
                {kpis.breakEvenVisitors != null ? formatNumber(kpis.breakEvenVisitors) : <span title={requiresTrafficCostTitle}>{notAvailable}</span>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionBreakEvenHelp", "Visitors to $0 profit")}</p>
            </div>
            <div className="rounded-lg border p-4" role="region" aria-labelledby="kpi-cm-label">
              <p id="kpi-cm-label" className="text-xs text-muted-foreground uppercase tracking-wide">{funnelText(content, "projectionContributionMargin", "Contribution Margin")}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">{kpis.contributionMarginPct != null ? formatPct(kpis.contributionMarginPct) : <span title={requiresTrafficCostTitle}>{notAvailable}</span>}</p>
              <p className="mt-1 text-xs text-muted-foreground">{funnelText(content, "projectionKpiTargetMargin", "Target: 50%+")}</p>
            </div>
          </div>
        ) : null}
      </section>

      {/* SECTION 4: Traffic Channel Breakdown */}
      {hasChannels && (
        <div>
          <h3 className="text-base font-semibold mb-3">{funnelText(content, "projectionTrafficBreakdownTitle", "Traffic Channel Breakdown")}</h3>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <caption className="sr-only">{funnelText(content, "projectionTrafficBreakdownCaption", "Traffic channel breakdown by visitors, cost, and ROAS")}</caption>
              <thead>
                <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                  <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionChannelColumn", "Channel")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionVisitorsColumn", "Visitors / mo")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionEstimatedCostColumn", "Est. Cost")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionCplColumn", "CPL")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionRoasColumn", "ROAS")}</th>
                </tr>
              </thead>
              <tbody>
                {trafficChannels.map((ch) => {
                  const visitorShare = totalVisitors > 0 ? ch.monthlyVisitors / totalVisitors : 0;
                  const leads = volumeMetrics.leads * visitorShare;
                  const cost = estimateChannelCost(ch, {
                    leads,
                    customers: volumeMetrics.customers * visitorShare,
                    grossRevenue: modeledGrossRevenue * visitorShare,
                  }) ?? 0;
                  const cpl = leads > 0 ? cost / leads : null;
                  const chRevenue = modeledGrossRevenue * visitorShare;
                  const chRoas = cost > 0 && chRevenue != null ? chRevenue / cost : null;
                  return (
                    <tr key={ch.id} className="border-b last:border-0">
                      <td className="px-3 py-2">{channelLabel(content, ch.id, ch.label)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(ch.monthlyVisitors)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {cost > 0 ? formatCurrency(cost) : notAvailable}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {cpl != null && cost > 0 ? formatCurrency(cpl) : notAvailable}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {chRoas != null ? chRoas.toFixed(2) + "x" : notAvailable}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-muted/30 font-semibold">
                  <td className="px-3 py-2">{funnelText(content, "projectionTotalLabel", "Total")}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNumber(totalVisitors)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(trafficCostTotal)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SECTION 5: Sensitivity Analysis */}
      <div>
        <h3 className="text-base font-semibold mb-1">{funnelText(content, "projectionLeverageTitle", "Funnel Leverage Analysis")}</h3>
        <p className="text-xs text-muted-foreground mb-3">
          {funnelText(content, "projectionLeverageHelp", "Which stages have the biggest impact on revenue?")}
        </p>
        {!hasProducts ? (
          <p className="text-sm text-muted-foreground">{funnelText(content, "projectionLeverageLocked", "Add products to see leverage analysis.")}</p>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <caption className="sr-only">{funnelText(content, "projectionLeverageCaption", "Funnel leverage analysis by stage conversion rate and revenue impact")}</caption>
              <thead>
                <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                  <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionStageColumn", "Stage")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionCurrentCvrColumn", "Current CVR")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionBenchmarkColumn", "Planner reference")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionGapColumn", "Gap")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionLiftColumn", "+10% Lifts Revenue")}</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionLeverageColumn", "Leverage")}</th>
                </tr>
              </thead>
              <tbody>
                {[...sensitivity]
                  .sort((a, b) => b.leverageScore - a.leverageScore)
                  .slice(0, 8)
                  .map((row) => {
                    const gapLabel =
                      row.gap === "above"
                        ? funnelText(content, "projectionGapAbove", "above")
                        : row.gap === "at"
                        ? funnelText(content, "projectionGapAt", "at reference")
                        : funnelText(content, "projectionGapBelow", "below");
                    const gapColor =
                      row.gap === "above"
                        ? "#22c55e"
                        : row.gap === "at"
                        ? "#eab308"
                        : "#ef4444";
                    return (
                      <tr key={row.stageId} className="border-b last:border-0">
                        <td className="px-3 py-2">{row.stageName}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatPct(row.currentCvr)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {formatPct(row.benchmarkCvr)}
                        </td>
                        <td className="px-3 py-2 text-right text-xs" style={{ color: gapColor }}>{gapLabel}</td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: "#22c55e" }}>
                          +{formatCurrency(row.delta10PctRevenueImpact)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {row.leverageScore.toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            <p className="border-t px-3 py-2 text-xs text-muted-foreground">
              {funnelText(content, "projectionPlanningReferenceNote", "Planner references are model assumptions for scenario comparison, not performance guarantees.")}
            </p>
          </div>
        )}
      </div>

      {/* SECTION 6: MRR Projection */}
      {hasProducts && hasSubscriptionProducts && kpis && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">
            {funnelText(content, "projectionMrrTitle", "MRR Growth Forecast (12 Months)")}
          </h3>
          <div className="space-y-4">
            {products
              .filter((p) => p.priceType === "monthly" || p.priceType === "yearly")
              .map((product) => {
                const mrrData = computeMrrProjection(product, monthlyNewCustomers, 12);
                const shownMonths = [1, 3, 6, 9, 12];
                return (
                  <div key={product.id}>
                    <p className="text-sm font-medium mb-2">{product.name}</p>
                    <div className="rounded-lg border overflow-hidden">
                      <table className="w-full text-sm">
                        <caption className="sr-only">{funnelText(content, "projectionMrrCaption", "MRR growth forecast showing active subscribers and monthly recurring revenue")}</caption>
                        <thead>
                          <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                            <th scope="col" className="text-start px-3 py-2 font-medium">{funnelText(content, "projectionMonthColumn", "Month")}</th>
                            <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionActiveSubscribersColumn", "Active Subscribers")}</th>
                            <th scope="col" className="text-right px-3 py-2 font-medium">{funnelText(content, "projectionMrrColumn", "MRR")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mrrData
                            .filter((row) => shownMonths.includes(row.month))
                            .map((row) => (
                              <tr key={row.month} className="border-b last:border-0">
                                <td className="px-3 py-2">{funnelText(content, "projectionMonthValue", "Month {month}", { month: row.month })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.activeSubscribers)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency(row.mrr)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      </div>
      )}

    </div>
  );
}
